import React, { useState, useEffect } from 'react';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  updateDoc, 
  arrayUnion, 
  serverTimestamp,
  query, // Added
  where, // Added
  getDocs, // Added
  deleteDoc // <-- Added: Import deleteDoc
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import {
  PlusIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  UserIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  BriefcaseIcon,
  ClipboardDocumentCheckIcon,
  LinkIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import PrequalificationDetailsView from './PrequalificationDetailsView';
import { getAuth, getIdToken } from 'firebase/auth'; // Added for document processing API

// Initialize Firestore and Storage
const db = getFirestore();
const storage = getStorage();
const auth = getAuth(); // Added for document processing API

// Deal card with file dropdown
// Consistent, initials-based avatar color derived from a string.
const avatarPalette = [
  'bg-indigo-100 text-indigo-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-sky-100 text-sky-700',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
];
const avatarClassFor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};
const initialsFor = (first = '', last = '') => {
  const a = (first || '').trim()[0] || '';
  const b = (last || '').trim()[0] || '';
  return (a + b).toUpperCase() || '—';
};

const DealCard = ({ deal, currentUser, formatCurrency, onDelete }) => {
  const submitterName = deal.submittedBy?.name || deal.brokerName;
  const [showFiles, setShowFiles] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoaded, setFilesLoaded] = useState(false);

  const loadFiles = async () => {
    if (filesLoaded) { setShowFiles(!showFiles); return; }
    try {
      const snap = await getDocs(query(collection(db, 'documentMetadata'), where('loanApplicationId', '==', deal.id)));
      setFiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFilesLoaded(true);
      setShowFiles(true);
    } catch (e) { console.error('Error loading files:', e); setShowFiles(true); setFilesLoaded(true); }
  };

  const docStatus = deal.allDocumentsUploaded
    ? { label: 'All files uploaded', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' }
    : deal.allPersonalFilesUploaded
      ? { label: 'Personal files complete', dot: 'bg-sky-500', pill: 'bg-sky-50 text-sky-700 ring-sky-600/20' }
      : { label: 'Awaiting documents', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700 ring-amber-600/20' };

  return (
    <div className="group bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-300 transition-all overflow-hidden">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-11 w-11 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ${avatarClassFor(`${deal.firstName}${deal.lastName}`)}`}>
              {initialsFor(deal.firstName, deal.lastName)}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{deal.firstName} {deal.lastName}</p>
              {deal.businessName && (
                <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                  <BuildingOffice2Icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                  {deal.businessName}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20 font-medium">
              <BriefcaseIcon className="h-3 w-3" />
              Full Deal
            </span>
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(deal)}
                title="Delete listing"
                aria-label="Delete listing"
                className="p-1.5 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Contact + price */}
        <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
          {deal.email && (
            <div className="flex items-center gap-2 text-gray-600 min-w-0">
              <EnvelopeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{deal.email}</span>
            </div>
          )}
          {deal.phoneNumber && (
            <div className="flex items-center gap-2 text-gray-600">
              <PhoneIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{deal.phoneNumber}</span>
            </div>
          )}
          {deal.purchasePrice && (
            <div className="flex items-center gap-2 text-gray-900 font-medium">
              <BanknotesIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span>{formatCurrency(deal.purchasePrice)}</span>
            </div>
          )}
        </div>

        {deal.notes && (
          <p className="mt-3 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">{deal.notes}</p>
        )}

        {/* Status pills */}
        <div className="mt-4 flex items-center flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ring-1 ring-inset ${docStatus.pill}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${docStatus.dot}`} />
            {docStatus.label}
          </span>
          {submitterName && (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-gray-100 text-gray-600">
              <UserIcon className="h-3 w-3" />
              {submitterName}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center gap-2">
        <button
          onClick={() => window.open(`/broker-deal/${deal.brokerId || currentUser.uid}?dealId=${deal.id}`, '_blank')}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-sm"
        >
          <ArrowUpTrayIcon className="h-3.5 w-3.5" />
          Upload More
        </button>
        <button
          onClick={() => { navigator.clipboard.writeText(`https://lended.ai/upload/${deal.id}`); alert('Upload link copied!'); }}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
        >
          <LinkIcon className="h-3.5 w-3.5" />
          Copy Link
        </button>
        <button
          onClick={loadFiles}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium ml-auto"
        >
          {showFiles ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
          Files {filesLoaded ? `(${files.length})` : ''}
        </button>
      </div>

      {showFiles && (
        <div className="px-5 py-3 border-t border-gray-100">
          {files.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No files uploaded yet</p>
          ) : (
            <div className="space-y-1.5">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircleIcon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span className="font-medium text-gray-700 truncate">{f.label || f.documentName}</span>
                  </div>
                  {f.fileUrl && (
                    <a href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 font-medium flex-shrink-0 ml-2">
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PrequalificationsTab = ({
  currentUser,
  prequalificationApplications,
  setPrequalificationApplications,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  selectedApplication,
  setSelectedApplication,
  showBusinessPrequalForm,
  setShowBusinessPrequalForm,
  handleNewBusiness,
  brokerDeals,
  standaloneBorrowers = [],
  onDeleteBrokerDeal,
  brokerData,
  showDeleteConfirmation,
  getStatusBadgeClass,
  formatStatusText,
  formatCurrency,
  formatDate,
  customLogo,
  onStandaloneBorrowerAdded
}) => {
  // Add state for view type

  // Add new state
  const [showPrequalifiedView, setShowPrequalifiedView] = useState(false);
  const [showBorrowerInvite, setShowBorrowerInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [borrowerInviteMode, setBorrowerInviteMode] = useState('upload');
  const [borrowerForm, setBorrowerForm] = useState({ firstName: '', lastName: '', email: '', phone: '', contactPreference: 'lended' });
  const [borrowerFiles, setBorrowerFiles] = useState({});
  const [borrowerSubmitting, setBorrowerSubmitting] = useState(false);
  const [borrowerSubmitMessage, setBorrowerSubmitMessage] = useState('');
  const [fileBorrower, setFileBorrower] = useState(null);
  const [borrowerFileType, setBorrowerFileType] = useState('Personal Financial Statement');
  const [borrowerFile, setBorrowerFile] = useState(null);
  const [borrowerFileUploading, setBorrowerFileUploading] = useState(false);
  const [borrowerFileMessage, setBorrowerFileMessage] = useState('');
  const effectiveBrokerId = brokerData?._effectiveBrokerId || currentUser?.uid || '';
  const borrowerInviteUrl = `${window.location.origin}/searchersignup?brokerId=${encodeURIComponent(effectiveBrokerId)}`;

  const copyBorrowerInvite = async () => {
    try {
      await navigator.clipboard.writeText(borrowerInviteUrl);
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 1800);
    } catch (error) {
      window.prompt('Copy this borrower signup link:', borrowerInviteUrl);
    }
  };

  const borrowerDocumentTypes = [
    'Personal Financial Statement',
    'Personal Tax Return 2025',
    'Personal Tax Return 2024',
    'Personal Tax Return 2023',
    'Resume',
  ];

  const submitStandaloneBorrower = async () => {
    const { firstName, lastName, email, phone, contactPreference } = borrowerForm;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      setBorrowerSubmitMessage('Enter the borrower’s name, email, and phone number.');
      return;
    }
    setBorrowerSubmitting(true);
    setBorrowerSubmitMessage('');
    try {
      const brokerName = brokerData?.brokerageName || `${brokerData?.firstName || ''} ${brokerData?.lastName || ''}`.trim() || 'Broker';
      const loanRef = await addDoc(collection(db, 'loanApplications'), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        phoneNumber: phone.trim(),
        status: 'Pre-lender',
        loanPurpose: 'Acquisition',
        businessName: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastUserUpdate: serverTimestamp(),
        personalGuarantors: [{ firstName: firstName.trim(), lastName: lastName.trim() }],
        source: 'broker_referral',
        brokerId: effectiveBrokerId,
        brokerName,
        submittedBy: { id: currentUser.uid, name: `${brokerData?.firstName || ''} ${brokerData?.lastName || ''}`.trim() || brokerName },
        submittedOnBehalfOfBorrower: true,
        contactPreference,
        called: false,
        allDocumentsUploaded: false,
      });

      for (const label of borrowerDocumentTypes) {
        const file = borrowerFiles[label];
        if (!file) continue;
        const storagePath = `broker-borrowers/${effectiveBrokerId}/${loanRef.id}/${label}/${file.name}`;
        const fileRef = ref(storage, storagePath);
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        await addDoc(collection(db, 'documentMetadata'), {
          documentName: label,
          label,
          fileName: file.name,
          fileUrl,
          uploadedAt: new Date().toISOString(),
          uploadLinkId: loanRef.id,
          loanApplicationId: loanRef.id,
          email: email.toLowerCase().trim(),
          uploadedBy: 'broker',
          brokerId: effectiveBrokerId,
          storagePath,
        });
      }

      await addDoc(collection(db, 'email'), {
        to: ['max@lended.ai', 'ben@lended.ai', 'isak@lended.ai'],
        from: 'Lended Bot <isak@lended.ai>',
        subject: `[Broker Prequal] ${firstName.trim()} ${lastName.trim()} from ${brokerName}`,
        message: {
          subject: `[Broker Prequal] ${firstName.trim()} ${lastName.trim()} from ${brokerName}`,
          html: `<p>A broker submitted a standalone borrower for prequalification.</p><ul><li>Borrower: ${firstName.trim()} ${lastName.trim()} (${email.toLowerCase().trim()})</li><li>Phone: ${phone.trim()}</li><li>Contact preference: <strong>${contactPreference === 'lended' ? 'Lended contacts borrower' : 'Broker contacts borrower'}</strong></li><li>Files uploaded: ${Object.values(borrowerFiles).filter(Boolean).length}</li></ul><p>Loan Application ID: ${loanRef.id}</p>`,
        },
      });

      onStandaloneBorrowerAdded?.({
        id: loanRef.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        phoneNumber: phone.trim(),
        phone: phone.trim(),
        status: 'Pre-lender',
        source: 'broker_referral',
        brokerId: effectiveBrokerId,
        businessName: 'Standalone prequalification',
        contactPreference,
        createdAt: new Date(),
      });

      setBorrowerSubmitMessage('Borrower added successfully.');
      setBorrowerForm({ firstName: '', lastName: '', email: '', phone: '', contactPreference: 'lended' });
      setBorrowerFiles({});
    } catch (error) {
      console.error('Error adding standalone borrower:', error);
      setBorrowerSubmitMessage(`Could not add borrower: ${error.message}`);
    } finally {
      setBorrowerSubmitting(false);
    }
  };

  const uploadBorrowerFile = async () => {
    if (!fileBorrower || !borrowerFile || !borrowerFileType) {
      setBorrowerFileMessage('Choose a document type and file.');
      return;
    }
    setBorrowerFileUploading(true);
    setBorrowerFileMessage('');
    try {
      const storagePath = `broker-borrowers/${effectiveBrokerId}/${fileBorrower.id}/${borrowerFileType}/${borrowerFile.name}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, borrowerFile);
      const fileUrl = await getDownloadURL(fileRef);
      await addDoc(collection(db, 'documentMetadata'), {
        documentName: borrowerFileType,
        label: borrowerFileType,
        fileName: borrowerFile.name,
        fileUrl,
        uploadedAt: new Date().toISOString(),
        uploadLinkId: fileBorrower.id,
        loanApplicationId: fileBorrower.id,
        email: (fileBorrower.email || '').toLowerCase().trim(),
        uploadedBy: 'broker',
        brokerId: effectiveBrokerId,
        storagePath,
      });
      await updateDoc(doc(db, 'loanApplications', fileBorrower.id), { updatedAt: serverTimestamp(), lastUserUpdate: serverTimestamp() });
      setBorrowerFile(null);
      setBorrowerFileMessage(`${borrowerFileType} uploaded successfully.`);
    } catch (error) {
      console.error('Error uploading borrower document:', error);
      setBorrowerFileMessage(`Upload failed: ${error.message}`);
    } finally {
      setBorrowerFileUploading(false);
    }
  };

  // Add a new state to track if we're viewing details
  const [viewingDetails, setViewingDetails] = useState(false);

  // Add new state variables
  const [prequalId, setPrequalId] = useState(null);
  const [activeStep, setActiveStep] = useState(1); // Track the active step
  
  // State to track status of individual uploads: { [key]: 'idle' | 'uploading' | 'uploaded' | 'error' | 'not_available', ... }
  // Key format: `${documentId}-${label}`
  const [uploadStatuses, setUploadStatuses] = useState({});

  // State for custom notifications
  const [notification, setNotification] = useState({ visible: false, type: '', message: '' });

  // State for accordion index
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);

  // State for loading indicator when fetching a single application for edit
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);

  // State for borrowers view
  const [showBorrowersView, setShowBorrowersView] = useState(false);
  const [showTeamPanel, setShowTeamPanel] = useState(false);
  const [teamInviteEmail, setTeamInviteEmail] = useState('');
  const [teamInviting, setTeamInviting] = useState(false);
  const [teamInviteMsg, setTeamInviteMsg] = useState('');

  // State for borrower loan applications (to check if they're pursuing other deals)
  const [borrowerLoanApps, setBorrowerLoanApps] = useState({});

  // Fetch loan applications for borrowers to check deal status
  useEffect(() => {
    const fetchBorrowerLoanApps = async () => {
      if (!prequalificationApplications || prequalificationApplications.length === 0) return;

      // Get all unique borrower emails
      const borrowerEmails = new Set();
      prequalificationApplications.forEach(app => {
        if (app.borrowers && Array.isArray(app.borrowers)) {
          app.borrowers.forEach(b => {
            if (b.email) borrowerEmails.add(b.email.toLowerCase());
          });
        }
      });

      if (borrowerEmails.size === 0) return;

      try {
        const loanAppsRef = collection(db, 'loanApplications');
        const loanAppsMap = {};

        // Fetch loan applications for each borrower email
        // Try both 'email' field and 'emails' array
        for (const email of borrowerEmails) {
          // First try direct email field
          let q = query(loanAppsRef, where('email', '==', email));
          let snapshot = await getDocs(q);

          // If not found, try emails array
          if (snapshot.empty) {
            q = query(loanAppsRef, where('emails', 'array-contains', email));
            snapshot = await getDocs(q);
          }

          if (!snapshot.empty) {
            const loanApp = snapshot.docs[0].data();
            const chosenBiz = loanApp.businesses?.[loanApp.chosenBusinessIndex];
            const dealSize = chosenBiz?.loanSize || chosenBiz?.projectedSalePrice || null;
            loanAppsMap[email] = {
              chosenBusinessIndex: loanApp.chosenBusinessIndex,
              chosenBusinessBrokerId: loanApp.chosenBusinessBrokerId,
              businesses: loanApp.businesses || [],
              dealStage: chosenBiz?.dealStage || loanApp.loanStatus || null,
              dealSize: dealSize ? Number(dealSize) : null,
              loanAmount: chosenBiz?.loanSize ? Number(chosenBiz.loanSize) : null
            };
          }
        }

        setBorrowerLoanApps(loanAppsMap);
      } catch (error) {
        console.error('Error fetching borrower loan apps:', error);
      }
    };

    fetchBorrowerLoanApps();
  }, [prequalificationApplications]);

  // Calculate summary statistics
  const totalApplications = prequalificationApplications.length;
  const totalPrequalifiedAmount = prequalificationApplications.reduce(
    (sum, app) => sum + (Number(app.prequalifiedAmount) || 0),
    0
  );

  // Calculate total borrowers from all prequalifications
  const allBorrowers = prequalificationApplications.reduce((acc, app) => {
    if (app.borrowers && Array.isArray(app.borrowers)) {
      app.borrowers.forEach(borrower => {
        // Check if this borrower is the chosen buyer for this prequalification
        const isChosenBuyer = app.chosenBuyer &&
          app.chosenBuyer.email?.toLowerCase() === borrower.email?.toLowerCase();

        // Check if borrower is pursuing a different deal (from their loan application)
        const borrowerEmail = borrower.email?.toLowerCase();
        const loanAppData = borrowerEmail ? borrowerLoanApps[borrowerEmail] : null;
        const hasChosenDeal = loanAppData &&
          loanAppData.chosenBusinessIndex !== null &&
          loanAppData.chosenBusinessIndex !== undefined;
        const isPursuingOtherDeal = hasChosenDeal &&
          loanAppData.chosenBusinessBrokerId !== currentUser?.uid;
        const chosenBusiness = hasChosenDeal ?
          loanAppData.businesses?.[loanAppData.chosenBusinessIndex] : null;
        const otherDealName = isPursuingOtherDeal ?
          (chosenBusiness?.businessName || chosenBusiness?.listingName || 'Another deal') : null;

        // Only show deal size and commission for borrowers with a deal in the works (green or purple)
        const hasDealInProgress = isChosenBuyer || isPursuingOtherDeal;
        const prequalAmount = app.projectedSalePrice || app.prequalifiedAmount || app.loanSize;
        const dealSize = hasDealInProgress
          ? (loanAppData?.dealSize || (prequalAmount ? Number(prequalAmount) : null))
          : null;
        const loanAmountForCommission = hasDealInProgress
          ? (loanAppData?.loanAmount || (prequalAmount ? Number(prequalAmount) : null))
          : null;

        acc.push({
          ...borrower,
          prequalificationId: app.id,
          businessName: app.businessName || 'N/A',
          isChosenBuyer: isChosenBuyer,
          chosenForDeal: isChosenBuyer ? app.businessName : null,
          isPursuingOtherDeal: isPursuingOtherDeal,
          otherDealName: otherDealName,
          dealStage: loanAppData?.dealStage || null,
          dealSize: dealSize,
          projectedCommission: loanAmountForCommission ? loanAmountForCommission * 0.005 : null
        });
      });
    }
    return acc;
  }, []);
  const totalBorrowers = allBorrowers.length;

  // Fetch prequalification applications
  const fetchPrequalificationApplications = async () => {
    if (!currentUser) return;
    
    try {
      const brokerDocRef = doc(db, 'brokers', currentUser.uid);
      const brokerDoc = await getDoc(brokerDocRef);
      
      if (brokerDoc.exists() && brokerDoc.data().prequalificationApplications) {
        const prequalIds = brokerDoc.data().prequalificationApplications;
        
        const prequalDocs = [];
        for (const prequalId of prequalIds) {
          const prequalDocRef = doc(db, 'prequalificationApplications', prequalId);
          const prequalDoc = await getDoc(prequalDocRef);
          
          if (prequalDoc.exists()) {
            prequalDocs.push({
              id: prequalDoc.id,
              ...prequalDoc.data()
            });
          }
        }
        
        setPrequalificationApplications(prequalDocs);
      }
    } catch (error) {
      console.error('Error fetching prequalification applications:', error);
    }
  };

  // Filter prequalification applications based on search, status, and date
  // Only show listings created after January 20, 2026
  const JAN_20_CUTOFF = new Date('2026-01-20T00:00:00');

  const filteredPrequalifications = prequalificationApplications
    .filter(app => {
      // Filter out listings created before January 20, 2026
      if (app.createdAt) {
        const createdDate = app.createdAt.toDate ? app.createdAt.toDate() : new Date(app.createdAt);
        if (createdDate < JAN_20_CUTOFF) {
          return false;
        }
      }

      // Filter by search query if one exists
      if (searchQuery && (!app.businessName || !app.businessName.toLowerCase().includes(searchQuery.toLowerCase()))) {
        return false;
      }

      // Filter by status if not "All"
      if (statusFilter !== 'All' && app.status !== statusFilter) {
        return false;
      }

      return true;
    });

  console.log('Filtered prequalifications:', filteredPrequalifications);

  // Modify the handleEdit function to show details instead of the form when prequalified
  // Also, fetch the latest data for the application before editing.
  const handleEdit = async (applicationId) => {
    setIsLoadingApplication(true); // Start loading indicator
    try {
      const appDocRef = doc(db, 'prequalificationApplications', applicationId);
      const appDoc = await getDoc(appDocRef);

      if (appDoc.exists()) {
        const applicationData = { id: appDoc.id, ...appDoc.data() };
        setSelectedApplication(applicationData);
      
      // If the application has a prequalifiedAmount, show details view instead of edit form
        if (applicationData.prequalifiedAmount) {
        setViewingDetails(true);
        setShowBusinessPrequalForm(false);
      } else {
        setViewingDetails(false);
        setShowBusinessPrequalForm(true);
      }
      } else {
        console.error("Application not found:", applicationId);
        // Optionally, show a notification to the user
        setNotification({ visible: true, type: 'error', message: 'Could not load application data. It may have been deleted.' });
        setTimeout(() => setNotification({ visible: false, type: '', message: '' }), 3000);
      }
    } catch (error) {
      console.error("Error fetching application for edit:", error);
      // Optionally, show an error notification
      setNotification({ visible: true, type: 'error', message: 'Error loading application details.' });
      setTimeout(() => setNotification({ visible: false, type: '', message: '' }), 3000);
    } finally {
      setIsLoadingApplication(false); // Stop loading indicator
    }
  };

  // Add a function to go back from details view
  const handleBackFromDetails = () => {
    setViewingDetails(false);
    setSelectedApplication(null);
  };

  // Add new PrequalifiedView component
  const PrequalifiedView = ({ application, onBack, formatCurrency }) => {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900">
                {application.businessName || 'Unnamed Business'}
              </h2>
              <button
                onClick={onBack}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            {/* Prequalified Amount Box */}
            <div className="border-2 border-dotted border-green-500 rounded-lg p-6 mb-8 bg-green-50">
              <div className="text-center">
                <h3 className="text-lg font-medium text-green-800 mb-2">Prequalified Amount</h3>
                <p className="text-3xl font-bold text-green-700">
                  {formatCurrency(application.prequalifiedAmount)}
                </p>
              </div>
            </div>

            {/* Add Potential Borrower Button */}
            <button
              className="w-full border-2 border-dotted border-gray-300 rounded-lg p-6 hover:bg-gray-50 transition-colors"
              onClick={() => {
                // Add your logic for handling potential borrower here
                console.log("Add potential borrower clicked");
              }}
            >
              <div className="text-center">
                <PlusIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-900">Add Potential Borrower</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Click here to add a potential borrower for this prequalification
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (showPrequalifiedView && selectedApplication) {
    return (
      <PrequalifiedView
        application={selectedApplication}
        onBack={() => {
          setShowPrequalifiedView(false);
          setSelectedApplication(null);
        }}
        formatCurrency={formatCurrency}
      />
    );
  }

  if (viewingDetails && selectedApplication) {
    return (
      <PrequalificationDetailsView 
        application={selectedApplication} 
        onBack={() => {
          setViewingDetails(false);
          setSelectedApplication(null);
        }}
        customLogo={customLogo}
      />
    );
  }

  if (showBusinessPrequalForm) {
    return (
      <BusinessPrequalificationForm 
        onCancel={() => {
          setShowBusinessPrequalForm(false);
          setSelectedApplication(null);
          // Refresh prequalification applications when returning from the form
          fetchPrequalificationApplications();
        }}
        currentUser={currentUser}
        brokerData={brokerData}
        initialData={selectedApplication}
      />
    );
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
      {/* Add a simple loading indicator for when an application is being fetched for edit */}
      {isLoadingApplication && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-lg shadow-xl">
            <p className="text-gray-700">Loading application data...</p>
            {/* You can add a spinner here */}
          </div>
        </div>
      )}

      {/* Borrowers View Modal */}
      {showBorrowersView && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">All Borrowers</h2>
              <button
                onClick={() => setShowBorrowersView(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {allBorrowers.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">No borrowers yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Borrowers will appear here when they sign up through your prequalification links.
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Email
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Phone
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Business
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Deal Size
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Proj. Commission
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Signed Up
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {allBorrowers.map((borrower, index) => (
                      <tr
                        key={index}
                        className={`${
                          borrower.isChosenBuyer
                            ? 'bg-green-50 hover:bg-green-100'
                            : borrower.isPursuingOtherDeal
                              ? 'bg-purple-50 hover:bg-purple-100'
                              : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            {borrower.firstName} {borrower.lastName}
                            {borrower.isChosenBuyer && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Chosen Buyer
                              </span>
                            )}
                            {borrower.isPursuingOtherDeal && !borrower.isChosenBuyer && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Pursuing Other Deal
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.email || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.phone || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.businessName || 'N/A'}
                          {borrower.isChosenBuyer && borrower.chosenForDeal && (
                            <span className="block text-xs text-green-600 mt-0.5">
                              Selected for this deal
                            </span>
                          )}
                          {borrower.isPursuingOtherDeal && borrower.otherDealName && (
                            <span className="block text-xs text-purple-600 mt-0.5">
                              Chose: {borrower.otherDealName}
                            </span>
                          )}
                          {(borrower.isChosenBuyer || borrower.isPursuingOtherDeal) && borrower.dealStage && (
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                              borrower.dealStage === 'Funded' || borrower.dealStage === 'funded'
                                ? 'bg-green-100 text-green-800'
                                : borrower.dealStage === 'Closing' || borrower.dealStage === 'closing'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-amber-100 text-amber-800'
                            }`}>
                              {borrower.dealStage}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.dealSize
                            ? `$${borrower.dealSize.toLocaleString()}`
                            : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.projectedCommission
                            ? <span className="text-green-600 font-medium">${borrower.projectedCommission.toLocaleString()}</span>
                            : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {borrower.signedUpAt ? new Date(borrower.signedUpAt).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            {brokerData?.firstName ? `Welcome back, ${brokerData.firstName}` : 'Deals'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            All your submitted full deals and prequalifications in one place.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowBorrowerInvite(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50"
          >
            <LinkIcon className="h-4 w-4 text-gray-400" />
            Add Borrower
          </button>
          <button
            onClick={() => setShowTeamPanel(prev => !prev)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50"
          >
            <UserIcon className="h-4 w-4 text-gray-400" />
            Manage Team
          </button>
          <button
            onClick={() => window.open(`/broker-deal/${currentUser.uid}`, '_blank')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50"
          >
            <BriefcaseIcon className="h-4 w-4 text-gray-400" />
            Submit Full Deal
          </button>
          <button
            onClick={handleNewBusiness}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="h-4 w-4" />
            Add Prequalification
          </button>
        </div>
      </div>

      {/* Stats hero */}
      {(() => {
        const fullDeals = brokerDeals?.length || 0;
        const prequalCount = (prequalificationApplications?.length || 0) + standaloneBorrowers.length;
        const isUnderwriting = (s) => {
          const v = (s || '').toString().toLowerCase();
          return v === 'underwriting' || v === 'in_underwriting' || v === 'in underwriting';
        };
        const dealStatus = (d) => d?.loanStatus || d?.status || '';
        const inUnderwriting = (brokerDeals || []).filter(d => isUnderwriting(dealStatus(d))).length;
        const inProgress = (brokerDeals || []).filter(d => {
          const v = dealStatus(d).toString().toLowerCase();
          if (!v) return false;
          if (isUnderwriting(v)) return false;
          return !['funded', 'rejected', 'complete'].includes(v);
        }).length;
        const cards = [
          { label: 'Full Deals', value: fullDeals, hint: 'Submitted with documents', icon: BriefcaseIcon, iconBg: 'bg-emerald-50 text-emerald-600', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
          { label: 'Prequalifications', value: prequalCount, hint: 'Active applications', icon: ClipboardDocumentCheckIcon, iconBg: 'bg-indigo-50 text-indigo-600', bar: 'bg-indigo-500', dot: 'bg-indigo-500' },
          { label: 'In Progress', value: inProgress, hint: 'Awaiting docs / review', icon: ClockIcon, iconBg: 'bg-amber-50 text-amber-600', bar: 'bg-amber-500', dot: 'bg-amber-500' },
          { label: 'In Underwriting', value: inUnderwriting, hint: 'With lender for review', icon: DocumentTextIcon, iconBg: 'bg-violet-50 text-violet-600', bar: 'bg-violet-500', dot: 'bg-violet-500' },
        ];
        const total = cards.reduce((sum, c) => sum + c.value, 0) || 1;
        return (
          <div className="mb-6 space-y-4">
            {/* Segmented status bar */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
              <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                {cards.map(c => (
                  c.value > 0 && (
                    <div key={c.label} className={c.bar} style={{ width: `${(c.value / total) * 100}%` }} title={`${c.label}: ${c.value}`} />
                  )
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {cards.map(c => (
                  <div key={c.label} className="flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                    <span className="text-sm text-gray-600">{c.label}</span>
                    <span className="text-sm font-semibold text-gray-900">{c.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {cards.map(c => {
                const Icon = c.icon;
                const pct = Math.round((c.value / total) * 100);
                return (
                  <div key={c.label} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.iconBg}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-xs font-medium text-gray-400">{pct}%</span>
                    </div>
                    <p className="mt-3 text-xs font-medium uppercase tracking-wider text-gray-400">{c.label}</p>
                    <p className="mt-1 text-3xl font-bold leading-none text-gray-900">{c.value}</p>
                    <p className="mt-1 text-xs text-gray-400">{c.hint}</p>
                    <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Team Panel */}
      {showTeamPanel && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
            <button onClick={() => setShowTeamPanel(false)} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Invite team members to access this portal. They'll create their own account and see all shared prequalifications and deals.</p>
          <div className="flex gap-3 mb-4">
            <input
              type="email"
              value={teamInviteEmail}
              onChange={(e) => setTeamInviteEmail(e.target.value)}
              placeholder="team@example.com"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              onClick={async () => {
                if (!teamInviteEmail.trim()) return;
                setTeamInviting(true);
                setTeamInviteMsg('');
                const inviteLink = `https://lended.ai/broker-signup?team=${currentUser.uid}&email=${encodeURIComponent(teamInviteEmail.trim().toLowerCase())}`;
                try {
                  await addDoc(collection(db, 'email'), {
                    to: teamInviteEmail.trim().toLowerCase(),
                    from: 'Isak @ Lended <isak@lended.ai>',
                    subject: `You've been invited to join a brokerage on Lended`,
                    message: {
                      subject: `You've been invited to join a brokerage on Lended`,
                      html: `<p>You've been invited to join a broker portal on Lended.</p>
<p><a href="${inviteLink}" style="display:inline-block;padding:12px 28px;background:#4f46e5;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Join Team</a></p>
<p>Once signed up, you'll have access to all shared prequalifications and deals.</p>
<p>Best,<br>Isak<br>(914) 414-1106<br>isak@lended.ai</p>`,
                    },
                  });
                  setTeamInviteMsg(`Invite sent to ${teamInviteEmail}`);
                  setTeamInviteEmail('');
                } catch (err) {
                  setTeamInviteMsg('Failed to send: ' + err.message);
                }
                setTeamInviting(false);
              }}
              disabled={teamInviting || !teamInviteEmail.trim()}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {teamInviting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
          {teamInviteMsg && <p className="text-sm text-green-600 mb-4">{teamInviteMsg}</p>}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-500 mb-1">Or share this invite link:</p>
            <div className="flex gap-2">
              <input readOnly value={`https://lended.ai/broker-signup?team=${currentUser.uid}`} className="flex-1 text-xs border border-gray-300 rounded-lg px-3 py-2 bg-white" />
              <button
                onClick={() => { navigator.clipboard.writeText(`https://lended.ai/broker-signup?team=${currentUser.uid}`); setTeamInviteMsg('Link copied!'); }}
                className="px-3 py-2 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Full Deals Section */}
      {brokerDeals && brokerDeals.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BriefcaseIcon className="h-5 w-5 text-emerald-600" />
              Full Deals Submitted
              <span className="text-sm font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{brokerDeals.length}</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brokerDeals.map(deal => (
              <DealCard key={deal.id} deal={deal} currentUser={currentUser} formatCurrency={formatCurrency} onDelete={onDeleteBrokerDeal} />
            ))}
          </div>
        </div>
      )}

      {standaloneBorrowers.length > 0 && (
        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <UserIcon className="h-5 w-5 text-blue-600" />
              Standalone Borrowers
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-500">{standaloneBorrowers.length}</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {standaloneBorrowers.map(borrower => (
              <div key={borrower.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold ${avatarClassFor(`${borrower.firstName}${borrower.lastName}`)}`}>{initialsFor(borrower.firstName, borrower.lastName)}</div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${borrower.allPersonalFilesUploaded ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{borrower.allPersonalFilesUploaded ? 'Files ready' : 'Awaiting files'}</span>
                  </div>
                  <h4 className="mt-4 truncate text-base font-bold text-gray-900">{`${borrower.firstName || ''} ${borrower.lastName || ''}`.trim() || 'Unnamed borrower'}</h4>
                  <div className="mt-3 space-y-2 text-sm text-gray-500">
                    <p className="flex items-center gap-2 truncate"><EnvelopeIcon className="h-4 w-4 flex-none text-gray-400" />{borrower.email || 'No email'}</p>
                    <p className="flex items-center gap-2"><PhoneIcon className="h-4 w-4 flex-none text-gray-400" />{borrower.phoneNumber || borrower.phone || 'No phone'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3 text-xs"><span className="text-gray-500">{borrower.contactPreference === 'broker' ? 'You contact' : 'Lended contacts'}</span><button type="button" onClick={() => { setFileBorrower(borrower); setBorrowerFile(null); setBorrowerFileMessage(''); }} className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 font-semibold text-blue-700 shadow-sm hover:bg-blue-50">Manage files</button></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prequalifications section header */}
      <div className="mt-8 mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardDocumentCheckIcon className="h-5 w-5 text-indigo-600" />
          Prequalifications
          <span className="text-sm font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{filteredPrequalifications.length}</span>
        </h3>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              placeholder="Search business names..."
            />
          </div>
          <select
            className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="applicationstarted">Application Started</option>
            <option value="underReview">Under Review</option>
            <option value="questions">Questions</option>
            <option value="preQualified">Prequalified</option>
          </select>
        </div>
      </div>

      {/* Empty state */}
      {filteredPrequalifications.length === 0 && (
        <div className="text-center py-10 mt-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No prequalifications</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new prequalification application.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleNewBusiness}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              New Prequalification
            </button>
          </div>
        </div>
      )}

      {/* Table View */}
      {filteredPrequalifications.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3 pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Business Name
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Prequalified Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Last Updated
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Submitted By
                  </th>
                  <th scope="col" className="relative py-3 pl-3 pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPrequalifications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50 transition-colors">
                    <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm">
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 flex-shrink-0 rounded-lg flex items-center justify-center text-xs font-semibold ${avatarClassFor(application.businessName || application.id)}`}>
                          {(application.businessName || 'U').trim()[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">{application.businessName || 'Unnamed Business'}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(application.status || 'applicationstarted')}`}>
                        {formatStatusText ? formatStatusText(application.status) : (application.status || 'New')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      {application.prequalifiedAmount ? formatCurrency(application.prequalifiedAmount) : <span className="text-gray-400 font-normal">N/A</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {application.lastUpdated ? formatDate(application.lastUpdated) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {application.submittedBy?.name ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                          <UserIcon className="h-3 w-3" />
                          {application.submittedBy.name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-6 text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(application.id)}
                        className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 mr-2"
                      >
                        View details
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(application)}
                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showBorrowerInvite && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/50 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Add a standalone borrower</h3>
                  <p className="mt-1 text-sm leading-6 text-gray-500">Add them yourself with their documents, or send them your signup link.</p>
                </div>
                <button onClick={() => setShowBorrowerInvite(false)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">×</button>
              </div>
            </div>
            <div className="border-b border-gray-100 px-6 pt-4">
              <div className="flex gap-6">
                <button onClick={() => setBorrowerInviteMode('upload')} className={`border-b-2 pb-3 text-sm font-semibold ${borrowerInviteMode === 'upload' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500'}`}>Upload for borrower</button>
                <button onClick={() => setBorrowerInviteMode('link')} className={`border-b-2 pb-3 text-sm font-semibold ${borrowerInviteMode === 'link' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500'}`}>Send signup link</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {borrowerInviteMode === 'link' ? (
                <>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your borrower signup link</label>
                  <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                    <input readOnly value={borrowerInviteUrl} className="min-w-0 flex-1 bg-transparent px-2 text-sm text-gray-700 outline-none" />
                    <button onClick={copyBorrowerInvite} className="whitespace-nowrap rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-500">{inviteCopied ? 'Copied!' : 'Copy link'}</button>
                  </div>
                  <div className="mt-4 rounded-xl bg-indigo-50 px-4 py-3 text-sm leading-6 text-indigo-900">When they sign up, their profile and prequalification application automatically appear under your brokerage.</div>
                </>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {[
                      ['firstName', 'First name', 'text'], ['lastName', 'Last name', 'text'],
                      ['email', 'Email', 'email'], ['phone', 'Phone number', 'tel'],
                    ].map(([key, label, type]) => (
                      <label key={key} className="block text-sm font-medium text-gray-700">{label} *
                        <input type={type} value={borrowerForm[key]} onChange={e => setBorrowerForm(v => ({ ...v, [key]: e.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
                      </label>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Who contacts the borrower?</label>
                    <div className="mt-2 grid grid-cols-2 rounded-xl bg-gray-100 p-1">
                      {[['lended', 'We contact'], ['broker', 'You contact']].map(([value, label]) => (
                        <button key={value} onClick={() => setBorrowerForm(v => ({ ...v, contactPreference: value }))} className={`rounded-lg px-4 py-2 text-sm font-medium ${borrowerForm.contactPreference === value ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500'}`}>{label}</button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">{borrowerForm.contactPreference === 'lended' ? 'Lended will reach out to the borrower directly.' : 'You will handle outreach; Lended stays behind the scenes.'}</p>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between"><label className="text-sm font-semibold text-gray-900">Prequalification documents</label><span className="text-xs text-gray-500">Upload what you have</span></div>
                    <div className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200">
                      {borrowerDocumentTypes.map(label => (
                        <label key={label} className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50">
                          <span className="min-w-0"><span className="block text-sm font-medium text-gray-700">{label}</span>{borrowerFiles[label] && <span className="block truncate text-xs text-emerald-600">{borrowerFiles[label].name}</span>}</span>
                          <span className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">{borrowerFiles[label] ? 'Replace' : 'Choose file'}</span>
                          <input type="file" className="sr-only" onChange={e => setBorrowerFiles(v => ({ ...v, [label]: e.target.files?.[0] || null }))} />
                        </label>
                      ))}
                    </div>
                  </div>
                  {borrowerSubmitMessage && <p className={`rounded-lg px-3 py-2 text-sm ${borrowerSubmitMessage.includes('successfully') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{borrowerSubmitMessage}</p>}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button onClick={() => setShowBorrowerInvite(false)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              {borrowerInviteMode === 'link' ? (
                <button onClick={() => window.open(borrowerInviteUrl, '_blank', 'noopener,noreferrer')} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800">Preview signup</button>
              ) : (
                <button onClick={submitStandaloneBorrower} disabled={borrowerSubmitting} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">{borrowerSubmitting ? 'Uploading…' : 'Add borrower'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {fileBorrower && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-950/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div><h3 className="text-lg font-semibold text-gray-900">Manage borrower files</h3><p className="mt-1 text-sm text-gray-500">{`${fileBorrower.firstName || ''} ${fileBorrower.lastName || ''}`.trim()} · {fileBorrower.email}</p></div>
              <button onClick={() => setFileBorrower(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100" aria-label="Close">×</button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <label className="block text-sm font-medium text-gray-700">Document type
                <select value={borrowerFileType} onChange={e => setBorrowerFileType(e.target.value)} className="mt-1 w-full rounded-xl border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500">
                  {borrowerDocumentTypes.map(type => <option key={type}>{type}</option>)}
                  <option>Other</option>
                </select>
              </label>
              <label className="block cursor-pointer rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-5 py-7 text-center hover:border-blue-400 hover:bg-blue-50/40">
                <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                <span className="mt-2 block text-sm font-semibold text-gray-700">{borrowerFile ? borrowerFile.name : 'Choose a file'}</span>
                <span className="mt-1 block text-xs text-gray-500">PDF, Word, Excel, or image</span>
                <input type="file" className="sr-only" onChange={e => setBorrowerFile(e.target.files?.[0] || null)} />
              </label>
              {borrowerFileMessage && <p className={`rounded-lg px-3 py-2 text-sm ${borrowerFileMessage.includes('successfully') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{borrowerFileMessage}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4"><button onClick={() => setFileBorrower(null)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">Done</button><button onClick={uploadBorrowerFile} disabled={!borrowerFile || borrowerFileUploading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">{borrowerFileUploading ? 'Uploading…' : 'Upload file'}</button></div>
          </div>
        </div>
      )}

      {/* Custom Notification Area */}
      {notification.visible && (
        <div 
          className={`fixed bottom-5 right-5 p-4 rounded-lg shadow-lg text-white z-50 transition-opacity duration-300 ${ 
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
};

// Business Prequalification Form Component
const BusinessPrequalificationForm = ({ onCancel, currentUser, brokerData, initialData }) => {
  const [businessName, setBusinessName] = useState('');
  const [businessState, setBusinessState] = useState('');
  const [industry, setIndustry] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [businessAge, setBusinessAge] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [licenseNeeded, setLicenseNeeded] = useState(false);
  // uploadedDocuments state will now store metadata fetched from prequalificationMetadata collection
  const [uploadedDocuments, setUploadedDocuments] = useState([]); 
  const [isLoading, setIsLoading] = useState(false);
  const [prequalId, setPrequalId] = useState(null);
  const [activeStep, setActiveStep] = useState(1); // Track the active step
  const [realEstateIncluded, setRealEstateIncluded] = useState(null); // 'yes' or 'no', or null if unanswered
  const [realEstateValue, setRealEstateValue] = useState('');
  const [hasOtherAddbacks, setHasOtherAddbacks] = useState(null); // 'yes' or 'no', or null if unanswered
  const [topCustomerConcentration, setTopCustomerConcentration] = useState(null); // Changed to null for 'yes' or 'no', or null if unanswered
  const [hasMultipleLLCs, setHasMultipleLLCs] = useState(null); // 'yes' or 'no', or null if unanswered - New State

  // State to track status of individual uploads: { [key]: 'idle' | 'uploading' | 'uploaded' | 'error' | 'not_available', ... }
  // Key format: `${documentId}-${label}`
  const [uploadStatuses, setUploadStatuses] = useState({});

  // Bulk upload state
  const [bulkQueue, setBulkQueue] = useState([]); // [{ file, label, customLabel, status, error }]
  const [bulkUploading, setBulkUploading] = useState(false);

  // Single-upload form state (mirrors broker page)
  const [simpleSelectedLabel, setSimpleSelectedLabel] = useState('');
  const [simpleCustomLabel, setSimpleCustomLabel] = useState('');
  const [simpleSelectedFile, setSimpleSelectedFile] = useState(null);
  const [simpleUploading, setSimpleUploading] = useState(false);

  // State for custom notifications
  const [notification, setNotification] = useState({ visible: false, type: '', message: '' });
  
  // State to track the open accordion item index
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null); // null means all are closed

  // Add autosave debounce timer state
  const [saveTimer, setSaveTimer] = useState(null);
  // Add saving indicator state
  const [autoSaving, setAutoSaving] = useState(false);

  // State for submission readiness and process
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Business prequalification documents
  const businessDocuments = [
    {
      DocumentID: 1,
      displayName: "2023 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2023",
      Labels: ["2023 Business Tax Return", "Set 2 - 2023 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 2,
      displayName: "2024 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2024",
      Labels: ["2024 Business Tax Return", "Set 2 - 2024 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 3,
      displayName: "2025 Business Tax Returns",
      documentName: "Business Tax Returns",
      Description: "Complete business tax returns for 2025",
      Labels: ["2025 Business Tax Return", "Set 2 - 2025 Business Tax Return"],
      AcceptedFileTypes: ".pdf,.jpg,.png"
    },
    {
      DocumentID: 4,
      displayName: "2026 Interim P&L (YTD)",
      documentName: "Interim P&L",
      Description: "Year-to-date Profit and Loss statement for 2026",
      Labels: ["2026 YTD P&L"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.jpg,.png"
    },
    {
      DocumentID: 5,
      displayName: "Trailing 12 Months P&L",
      documentName: "Trailing 12 Months P&L",
      Description: "Rolling 12-month Profit and Loss statement",
      Labels: ["Trailing 12 Months P&L"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.jpg,.png"
    },
    {
      DocumentID: 6,
      displayName: "Balance Sheet",
      documentName: "Balance Sheet",
      Description: "Most recent balance sheet",
      Labels: ["Balance Sheet"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.jpg,.png"
    },
    {
      DocumentID: 7,
      displayName: "Confidential Information Memorandum",
      documentName: "CIM",
      Description: "CIM if available",
      Labels: ["CIM Document"],
      AcceptedFileTypes: ".pdf,.doc,.docx"
    },
    {
      DocumentID: 8,
      displayName: "Letter of Intent (LOI)",
      documentName: "LOI",
      Description: "Signed or draft Letter of Intent",
      Labels: ["LOI"],
      AcceptedFileTypes: ".pdf,.doc,.docx"
    }
  ];

  // Define conditional documents meta data - These will be added dynamically
  const conditionalDocuments = {
    license: {
      DocumentID: 101, // Assign unique IDs
      displayName: "Business License",
      documentName: "Other", // No specific category for business license in the list
      Description: "Upload the relevant business license if required",
      Labels: ["Business License"],
      AcceptedFileTypes: ".pdf,.jpg,.png,.doc,.docx"
    },
    appraisal: {
      DocumentID: 102,
      displayName: "Real Estate Appraisal",
      documentName: "Other", // No specific category for real estate appraisal in the list
      Description: "Upload the real estate appraisal if applicable",
      Labels: ["Real Estate Appraisal"],
      AcceptedFileTypes: ".pdf"
    },
    addbacks: {
      DocumentID: 103,
      displayName: "Addbacks Schedule",
      documentName: "AddBack Sheet",
      Description: "Upload a schedule detailing non-standard addbacks (must be verifiable)",
      Labels: ["Addbacks Schedule"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.doc,.docx"
    },
    customerConcentration: {
      DocumentID: 104,
      displayName: "Customer Concentration Details",
      documentName: "Customer Concentration",
      Description: "Upload a list of top 10 customers by % of revenue and/or description of any concentrations",
      Labels: ["Top 10 Customer List"],
      AcceptedFileTypes: ".pdf,.xls,.xlsx,.doc,.docx"
    }
  };

  // Define Other Important Documentation - Added
  const otherDocuments = {
    other: {
      DocumentID: 999, // Assign a unique ID
      displayName: "Other Important Documentation",
      documentName: "Other",
      Description: "Upload any other relevant documents",
      Labels: ["Other Important Documents"], // Use a single label for now
      AcceptedFileTypes: "*/*" // Allow any file type
    }
  };

  const formatCurrency = (value) => {
    // Check if value is a string, if not convert it to a string
    const stringValue = String(value || '');
    // Remove all non-digits
    const digits = stringValue.replace(/\D/g, '');
    // Format with commas
    return digits ? `$${Number(digits).toLocaleString()}` : '';
  };

  // Function to show notifications
  const showNotification = (type, message) => {
    setNotification({ visible: true, type, message });
    setTimeout(() => {
      setNotification({ visible: false, type: '', message: '' });
    }, 3000); // Hide after 3 seconds
  };

  // Function to fetch metadata from the new collection
  const fetchPrequalificationMetadata = async (applicationId) => {
    if (!applicationId) return;
    console.log("Fetching metadata for application:", applicationId);
    try {
      const metadataQuery = query(
        collection(db, 'prequalificationMetadata'), 
        where('applicationId', '==', applicationId)
      );
      const querySnapshot = await getDocs(metadataQuery);
      const fetchedMetadata = [];
      querySnapshot.forEach((doc) => {
        fetchedMetadata.push({ id: doc.id, ...doc.data() }); // Include Firestore doc ID if needed
      });
      console.log("Fetched metadata:", fetchedMetadata);
      setUploadedDocuments(fetchedMetadata); // Update state with fetched data
    } catch (error) {
      console.error('Error fetching prequalification metadata:', error);
      showNotification('error', 'Failed to load document statuses.');
    }
  };

  // *** Added Helper Function ***
  // Maps specific document definitions in this component to a general category name
  const getGeneralDocumentName = (document) => {
    if (!document || !document.Name) return 'Other'; // Default safety

    const name = document.Name.toLowerCase();
    const id = document.DocumentID;

    // Check specific IDs or name patterns
    if (id === 1 || id === 2 || id === 3 || name.includes('business tax return')) {
      return 'Business Tax Returns';
    }
    if (id === 4 || name.includes('profit & loss') || name.includes('p&l')) {
      return 'PnL';
    }
    if (id === 5 || name.includes('balance sheet')) {
      return 'Balance Sheet';
    }
    if (id === 6 || name.includes('confidential information memorandum') || name.includes('cim')) {
      return 'CIM';
    }
    if (id === conditionalDocuments.addbacks.DocumentID || name.includes('addbacks schedule')) {
      return 'AddBack Sheet';
    }
    if (id === conditionalDocuments.customerConcentration.DocumentID || name.includes('customer concentration details')) {
      return 'Customer Concentration';
    }
     if (id === otherDocuments.other.DocumentID || name.includes('other important documentation')) {
      return 'Other';
    }
    // For types not explicitly mapped in the user's list, use their specific Name for now
    if (id === conditionalDocuments.license.DocumentID || name.includes('business license')) {
       return 'Business License'; // Keeping specific as per analysis
    }
    if (id === conditionalDocuments.appraisal.DocumentID || name.includes('real estate appraisal')) {
       return 'Real Estate Appraisal'; // Keeping specific as per analysis
    }

    // Fallback if none match - use the specific name defined in the component
    console.warn(`Unmapped document type for general name: ${document.Name} (ID: ${id}). Using specific name.`);
    return document.Name; 
  };
  // *** End Helper Function ***

  const handleFileUpload = async (event, documentId, label) => {
    if ((!event.target.files || !event.target.files[0]) && (!event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files[0])) {
      console.log("No file provided in event");
      return; // Exit if no file is found in either target or dataTransfer
    }
    if (!prequalId) {
      console.log("Prequalification ID not set");
      return; // Exit if prequalId is not set
    }

    const file = event.target.files ? event.target.files[0] : event.dataTransfer.files[0];
    
    // --- Fix: Search both standard and conditional documents ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    // --- Modification Start: Add check for otherDocuments ---
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }
    // --- Modification End ---

    if (!document) {
      console.error("Document definition not found for ID:", documentId);
      showNotification('error', `Internal error: Document definition not found.`);
      return; // Exit if document definition is not found
    }

    const uploadKey = `${documentId}-${label}`;

    // Set status to uploading
    setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'uploading' }));
    setIsLoading(true); // Keep general loading state for the submit button
    
    try {
      // Create storage path with prequalId for organization
      const storageRef = ref(storage, 
        `prequalifications/${currentUser.uid}/${prequalId}/${document.displayName}/${label}/${file.name}`
      );

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Create metadata for the new collection
      const metadata = {
        applicationId: prequalId,
        brokerId: currentUser.uid,
        // --- Updated: Use direct documentName property ---
        documentName: document.documentName, 
        fileName: file.name,
        fileUrl: downloadURL,
        // --- Label remains the specific one ---
        label: label, 
        uploadedAt: new Date().toISOString(), // As per user example
        status: 'uploaded' // Explicit status
      };

      // --- Refactor Start: Save metadata to new collection ---
      // Remove previous logic of adding to local state immediately
      // Add the metadata document to the 'prequalificationMetadata' collection
      const docRef = await addDoc(collection(db, 'prequalificationMetadata'), metadata);
      console.log(`Metadata saved to prequalificationMetadata for ${label} with ID: ${docRef.id}`);
      // --- Refactor End ---

      // Update the lastUpdated timestamp on the main application document
      // AND add the document to the documents array
      const prequalRef = doc(db, 'prequalificationApplications', prequalId);
      await updateDoc(prequalRef, {
        lastUpdated: serverTimestamp(),
        documents: arrayUnion({
          documentName: document.documentName,
          fileName: file.name,
          fileUrl: downloadURL,
          label: label,
          uploadedAt: new Date().toISOString(),
          status: 'uploaded',
          brokerId: currentUser.uid
        })
      });

      // Set status to uploaded
      setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'uploaded' }));

      // --- Add API Integration for Document Processing ---
      try {
        // Prepare request data
        const requestBody = {
          fileUrl: downloadURL,
          metadata: {
            ...metadata,
            documentId: docRef.id, // Include the Firestore document ID
            documentType: document.documentName // Include document type for the API
          }
        };
        
        // Get auth token
        const idToken = await getIdToken(auth.currentUser);
        
        // Make API call to process document
        const apiEndpoint = 'https://sba2-7f2ba300d905.herokuapp.com/process-document'; // Using the one from documentTracker.js
        console.log('Sending document for processing:', { ...requestBody, fileUrl: '[URL REDACTED]' });
        
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        
        console.log('API response status:', response.status);
        
        if (response.ok) {
          console.log('Document processed successfully');
          const responseData = await response.json();
          console.log('Processing result:', responseData);
          
          // You could update metadata here with extracted data if needed
          
          showNotification('success', `'${label}' uploaded and processed successfully!`);
        } else {
          console.error('Error processing document:', await response.text());
          // Still show a success message for upload since the file was uploaded successfully
          showNotification('success', `'${label}' uploaded successfully, but processing failed.`);
        }
      } catch (apiError) {
        console.error('API processing error:', apiError);
        // We don't want to fail the whole upload if only the processing fails
        showNotification('success', `'${label}' uploaded successfully, but processing failed: ${apiError.message}`);
      }
      // --- End API Integration ---

      // --- Refactor: Fetch updated metadata to refresh state ---
      await fetchPrequalificationMetadata(prequalId);

    } catch (error) {
      console.error('Error uploading file:', error);
      // Set status to error and show error notification
      setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'error' }));
      showNotification('error', `Error uploading '${label}'. Please try again.`);
    } finally {
      // Check if any uploads are still in progress before setting isLoading to false
      // We need to check uploadStatuses *after* potential state updates from fetch
      const stillUploading = Object.values(uploadStatuses).some(status => status === 'uploading');
       if (!stillUploading) {
          // Re-evaluate loading state after potential fetch
          const currentStatuses = { ...uploadStatuses }; // Get potentially updated statuses
          const isLoadingStill = Object.values(currentStatuses).some(s => s === 'uploading');
          setIsLoading(isLoadingStill);
       }
    }
  };

  // ---- Bulk upload (mirror of broker page) ----
  // Guess doc label from filename
  const guessPrequalDocLabel = (filename) => {
    const n = filename.toLowerCase();
    const yearMatch = n.match(/20(2[2-6])/);
    const year = yearMatch ? `20${yearMatch[1]}` : null;
    if (/p[\s_-]*&?[\s_-]*l|profit[\s_-]*and[\s_-]*loss/.test(n)) {
      if (n.includes('trailing') || n.includes('ttm') || n.includes('t12')) return 'Trailing 12 Months P&L';
      if (year === '2026' || n.includes('ytd') || n.includes('interim')) return '2026 YTD P&L';
    }
    if (/tax[\s_-]*return|1120|1065/.test(n) && year) return `${year} Business Tax Return`;
    if (/balance[\s_-]*sheet/.test(n)) return 'Balance Sheet';
    if (/cim|memorandum/.test(n)) return 'CIM Document';
    if (/loi|letter[\s_-]*of[\s_-]*intent/.test(n)) return 'LOI';
    if (/appraisal|valuation/.test(n)) return 'Real Estate Appraisal';
    if (/license/.test(n)) return 'Business License';
    if (/addback/.test(n)) return 'Addbacks Schedule';
    if (/customer[\s_-]*concentration/.test(n)) return 'Top 10 Customer List';
    return '';
  };

  // Build the active label list (required + conditional based on form answers)
  const buildPrequalDocOptions = () => {
    const all = [];
    businessDocuments.forEach(d => d.Labels.forEach(l => all.push({ documentName: d.documentName, displayName: d.displayName, label: l })));
    if (licenseNeeded) conditionalDocuments.license.Labels.forEach(l => all.push({ documentName: conditionalDocuments.license.documentName, displayName: conditionalDocuments.license.displayName, label: l }));
    if (realEstateIncluded === 'yes') conditionalDocuments.appraisal.Labels.forEach(l => all.push({ documentName: conditionalDocuments.appraisal.documentName, displayName: conditionalDocuments.appraisal.displayName, label: l }));
    if (hasOtherAddbacks === 'yes') conditionalDocuments.addbacks.Labels.forEach(l => all.push({ documentName: conditionalDocuments.addbacks.documentName, displayName: conditionalDocuments.addbacks.displayName, label: l }));
    if (topCustomerConcentration === 'yes') conditionalDocuments.customerConcentration.Labels.forEach(l => all.push({ documentName: conditionalDocuments.customerConcentration.documentName, displayName: conditionalDocuments.customerConcentration.displayName, label: l }));
    // Filter out "Set 2" labels unless multi-LLC
    return all.filter(o => !(o.label.startsWith('Set 2') && hasMultipleLLCs !== 'yes'));
  };

  const handleBulkFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!prequalId) {
      showNotification('error', 'Save the prequalification basics first (Step 1) before uploading documents.');
      return;
    }
    setBulkQueue(Array.from(fileList).map(file => ({
      file,
      label: guessPrequalDocLabel(file.name),
      customLabel: '',
      status: 'pending',
      error: '',
    })));
  };

  const updateBulkRow = (idx, patch) => {
    setBulkQueue(prev => prev.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const removeBulkRow = (idx) => {
    setBulkQueue(prev => prev.filter((_, i) => i !== idx));
  };

  const bulkUploadOne = async (file, label) => {
    // Match the existing per-doc storage layout
    const allDocs = [...businessDocuments, ...Object.values(conditionalDocuments), otherDocuments.other];
    const docDef = allDocs.find(d => d.Labels.includes(label)) || otherDocuments.other;
    const storagePath = `prequalifications/${currentUser.uid}/${prequalId}/${docDef.displayName}/${label}/${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    const metadata = {
      applicationId: prequalId,
      brokerId: currentUser.uid,
      documentName: docDef.documentName,
      fileName: file.name,
      fileUrl: downloadURL,
      label,
      uploadedAt: new Date().toISOString(),
      status: 'uploaded',
    };
    await addDoc(collection(db, 'prequalificationMetadata'), metadata);
    const prequalRef = doc(db, 'prequalificationApplications', prequalId);
    await updateDoc(prequalRef, {
      lastUpdated: serverTimestamp(),
      documents: arrayUnion({ ...metadata, brokerId: currentUser.uid }),
    });
  };

  const startBulkUpload = async () => {
    setBulkUploading(true);
    for (let i = 0; i < bulkQueue.length; i++) {
      const row = bulkQueue[i];
      if (row.status === 'done') continue;
      const label = (row.customLabel && row.customLabel.trim()) || row.label;
      if (!label) { updateBulkRow(i, { status: 'error', error: 'Pick a doc type or give it a custom name' }); continue; }
      updateBulkRow(i, { status: 'uploading', error: '' });
      try {
        await bulkUploadOne(row.file, label);
        updateBulkRow(i, { status: 'done' });
      } catch (err) {
        updateBulkRow(i, { status: 'error', error: err.message || 'Upload failed' });
      }
    }
    await fetchPrequalificationMetadata(prequalId);
    setBulkUploading(false);
  };

  const closeBulkModal = () => {
    if (bulkUploading) return;
    setBulkQueue([]);
  };

  const handleSimpleUpload = async () => {
    const label = simpleCustomLabel.trim() || simpleSelectedLabel;
    if (!simpleSelectedFile || !label || !prequalId) return;
    setSimpleUploading(true);
    try {
      await bulkUploadOne(simpleSelectedFile, label);
      await fetchPrequalificationMetadata(prequalId);
      showNotification('success', `${label} uploaded`);
      setSimpleSelectedFile(null);
      setSimpleSelectedLabel('');
      setSimpleCustomLabel('');
      const fileInput = document.getElementById('prequal-file-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      showNotification('error', err.message || 'Upload failed');
    } finally {
      setSimpleUploading(false);
    }
  };

  // Handle clicking "Do Not Have"
  const handleDoNotHave = async (documentId, label) => { // Made async
    // Find the document definition (could be in businessDocuments or conditionalDocuments)
    // --- Modification Start: Include otherDocuments ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }
    // --- Modification End ---

    if (!document) {
      console.error("Document definition not found for ID:", documentId);
      return; // Should not happen if IDs are correct
    }

    const uploadKey = `${documentId}-${label}`;

    // Set status in uploadStatuses state
    setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'not_available' }));

    // Create metadata for the 'not_available' status for the new collection
    const metadata = {
      applicationId: prequalId,
      brokerId: currentUser.uid,
      // --- Updated: Use direct documentName property ---
      documentName: document.documentName,
      fileName: null, // No file
      fileUrl: null, // No URL
      // --- Label remains the specific one ---
      label: label, 
      markedAt: new Date().toISOString(), // Use markedAt
      status: 'not_available'
    };

    // --- Refactor Start: Save "Not Available" status to new collection ---
    // Remove previous logic of adding to local state immediately
     try {
       // Check if a record for this label already exists (uploaded or N/A) and remove/update if necessary,
       // or decide on strategy (e.g., allow multiple N/A entries vs overwrite).
       // For simplicity here, we'll just add a new entry. Consider adding cleanup/overwrite logic if needed.
       await addDoc(collection(db, 'prequalificationMetadata'), metadata);
       console.log(`'Not Available' status saved to prequalificationMetadata for ${label}`);

       // Update the lastUpdated timestamp on the main application document
       // AND add the document to the documents array
       const prequalRef = doc(db, 'prequalificationApplications', prequalId);
       await updateDoc(prequalRef, {
         lastUpdated: serverTimestamp(),
         documents: arrayUnion({
           documentName: document.documentName,
           fileName: null,
           fileUrl: null,
           label: label,
           markedAt: new Date().toISOString(),
           status: 'not_available',
           brokerId: currentUser.uid
         })
       });

       // Optional: Show a notification
       showNotification('info', `'${label}' marked as 'Not Available'.`);

       // Fetch updated metadata
       await fetchPrequalificationMetadata(prequalId);

     } catch (error) {
       console.error("Error saving 'Not Available' status:", error);
       showNotification('error', `Failed to mark '${label}' as Not Available.`);
       // Revert local status indicator if save failed
       setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'idle' })); // Or previous state
     }
    // --- Refactor End ---
  };

  // isDocumentUploaded and isDocumentNotAvailable now rely on the fetched uploadedDocuments state
  const isDocumentUploaded = (documentId, label) => {
    // --- Modification Start: Include otherDocuments & check 'some' for 'Other' ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
     if (!document) {
       document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
     }
     if (!document && documentId === otherDocuments.other.DocumentID) {
       document = otherDocuments.other;
     }
     if (!document) return false;

     if (documentId === otherDocuments.other.DocumentID) {
       // For 'Other', check if at least one document with 'uploaded' status exists for this label
       return uploadedDocuments.some(
         doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
       );
     } else {
       // For other types, check if the specific entry exists and is 'uploaded'
       const docEntry = uploadedDocuments.find(
         doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
       );
       return !!docEntry; // Return true if such an entry exists
     }
     // --- Modification End ---
  };

  // Check if a document is marked as 'Not Available'
  const isDocumentNotAvailable = (documentId, label) => {
     // --- Modification Start: Include otherDocuments ---
     let document = businessDocuments.find(doc => doc.DocumentID === documentId);
     if (!document) {
       document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
     }
     if (!document && documentId === otherDocuments.other.DocumentID) {
       document = otherDocuments.other;
     }
     // --- Modification End ---
     if (!document) return false;

     // Find *any* entry for this label marked as 'not_available'
     const docEntry = uploadedDocuments.find(
       doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'not_available'
     );
     // Check if there's an entry and its status is 'not_available'
     return !!docEntry; // Return true if such an entry exists
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!businessName) {
      alert('Please enter a business name');
      return;
    }
    
    setIsLoading(true);
    try {
      // Update existing document (since it was already created)
      const prequalRef = doc(db, 'prequalificationApplications', initialData.id);
      await updateDoc(prequalRef, {
        businessName,
        businessState,
        industry,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes', // Save addbacks status
        topCustomerConcentration: topCustomerConcentration === 'yes', // Modified to save boolean
        hasMultipleLLCs: hasMultipleLLCs === 'yes', // Save multiple LLCs status - Added
        status: 'In progress',
        lastUpdated: serverTimestamp(),
        // --- Refactor: Remove documents array ---
        // documents: uploadedDocuments // NO LONGER SAVED HERE
      });
      
      alert('Prequalification application updated successfully!');
      onCancel(); // Return to list view
    } catch (error) {
      console.error('Error updating prequalification:', error);
      alert('Error updating prequalification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePrequalification = async () => {
    if (!currentUser) return;
    
    try {
      const initialData = {
        businessName,
        businessState,
        industry,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes', // Save addbacks status
        topCustomerConcentration: topCustomerConcentration === 'yes', // Modified to save boolean
        hasMultipleLLCs: hasMultipleLLCs === 'yes', // Save multiple LLCs status - Added
        status: 'applicationstarted', // Updated initial status
        brokerId: currentUser.uid,
        submittedBy: {
          uid: currentUser.uid,
          name: `${brokerData?.firstName || ''} ${brokerData?.lastName || ''}`.trim()
            || brokerData?.brokerAgeName
            || brokerData?.name
            || brokerData?.email
            || 'Team Member',
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        // --- Refactor: Remove documents array ---
        // documents: [] // NO LONGER NEEDED HERE
      };
      
      const docRef = await addDoc(collection(db, 'prequalificationApplications'), initialData);
      console.log('Created new prequalification document with ID:', docRef.id);
      setPrequalId(docRef.id);
      
      // Add this ID to the broker's prequalification list
      const brokerRef = doc(db, 'brokers', currentUser.uid);
      await updateDoc(brokerRef, {
        prequalificationApplications: arrayUnion(docRef.id)
      });
      
      alert('Prequalification started successfully!');
    } catch (error) {
      console.error('Error creating prequalification document:', error);
      alert('Error starting prequalification. Please try again.');
    }
  };

  // Get document file URL
  const getDocumentFileUrl = (documentId, label) => {
    // --- Fix: Search both standard and conditional documents ---
    let document = businessDocuments.find(doc => doc.DocumentID === documentId);
    if (!document) {
      document = Object.values(conditionalDocuments).find(doc => doc.DocumentID === documentId);
    }
    if (!document && documentId === otherDocuments.other.DocumentID) {
      document = otherDocuments.other;
    }    
    // --- End Fix ---
    
    if (!document) return null; // Return null if definition not found in either list
    
    // Find the corresponding document in the fetched metadata state
    const uploadedDoc = uploadedDocuments.find(
      doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'uploaded'
    );
    
    return uploadedDoc ? uploadedDoc.fileUrl : null;
  };

  useEffect(() => {
    console.log("BusinessPrequalificationForm initialData:", initialData);
    
    if (initialData && initialData.id) {
      console.log("Setting form data from initialData");
      setBusinessName(initialData.businessName || '');
      setBusinessState(initialData.businessState || '');
      setIndustry(initialData.industry || '');
      // Make sure purchasePrice is properly handled even if it's a number
      setPurchasePrice(initialData.purchasePrice ? formatCurrency(initialData.purchasePrice) : '');
      setBusinessAge(initialData.businessAge || '');
      setBusinessDescription(initialData.businessDescription || '');
      setLicenseNeeded(initialData.licenseNeeded || false);
      setPrequalId(initialData.id);
      
      // Set real estate fields from initialData
      if (initialData.realEstateIncluded !== undefined) {
        setRealEstateIncluded(initialData.realEstateIncluded ? 'yes' : 'no');
      } else {
        setRealEstateIncluded(null);
      }
      setRealEstateValue(initialData.realEstateValue ? formatCurrency(initialData.realEstateValue) : '');

      // Set addbacks status from initialData
      if (initialData.hasOtherAddbacks !== undefined) {
        setHasOtherAddbacks(initialData.hasOtherAddbacks ? 'yes' : 'no');
      } else {
        setHasOtherAddbacks(null);
      }

      // Set multiple LLCs status from initialData - Added
      if (initialData.hasMultipleLLCs !== undefined) {
        setHasMultipleLLCs(initialData.hasMultipleLLCs ? 'yes' : 'no');
      } else {
        setHasMultipleLLCs(null);
      }

      // Set customer concentration from initialData - Modified
      if (initialData.topCustomerConcentration !== undefined) {
        setTopCustomerConcentration(initialData.topCustomerConcentration ? 'yes' : 'no');
      } else {
        setTopCustomerConcentration(null);
      }

      // --- Refactor: Fetch metadata instead of using initialData.documents ---
      if (initialData.id) {
        fetchPrequalificationMetadata(initialData.id);
      } else {
        // Clear documents if there's no initial data or ID
        setUploadedDocuments([]);
      }
      // Remove the direct setting from initialData.documents
      // if (initialData.documents && Array.isArray(initialData.documents)) {
      //   console.log("Setting uploaded documents from initialData:", initialData.documents);
      //   setUploadedDocuments(initialData.documents); // NO LONGER DONE HERE
      // }
    } else {
       // Clear form and documents if no initialData
       setBusinessName('');
       setPurchasePrice('');
       setBusinessAge('');
       setBusinessDescription('');
       setLicenseNeeded(false);
       setPrequalId(null);
       setRealEstateIncluded(null);
       setRealEstateValue('');
       setHasOtherAddbacks(null);
       setHasMultipleLLCs(null);
       setTopCustomerConcentration(null);
       setUploadedDocuments([]); // Clear documents
    }
  }, [initialData]); // Add fetchPrequalificationMetadata dependency if needed, but it's called inside

  // Calculate progress percentage
  const calculateProgress = () => {
    let filledFields = 0;
    let totalFields = 5; // Keeping the same total fields count
    
    if (businessName) filledFields++;
    if (purchasePrice) filledFields++;
    if (businessAge) filledFields++;
    if (businessDescription) filledFields++;
    if (topCustomerConcentration !== null) filledFields++; // Changed to check if not null
    
    // Add document uploads to progress calculation
    // This part might need adjustment based on how 'completeness' is defined with the new structure.
    // For now, let's count distinct labels that are either 'uploaded' or 'not_available'.
    const requiredLabels = new Set();
    businessDocuments.forEach(doc => doc.Labels.forEach(label => requiredLabels.add(label)));
    if (licenseNeeded) conditionalDocuments.license.Labels.forEach(label => requiredLabels.add(label));
    if (realEstateIncluded === 'yes') conditionalDocuments.appraisal.Labels.forEach(label => requiredLabels.add(label));
    if (hasOtherAddbacks === 'yes') conditionalDocuments.addbacks.Labels.forEach(label => requiredLabels.add(label));
    if (topCustomerConcentration === 'yes') conditionalDocuments.customerConcentration.Labels.forEach(label => requiredLabels.add(label));
    // Add 'Other' if needed for progress calculation, or exclude it. Let's exclude for now.

    const completedDocs = new Set(
        uploadedDocuments
            .filter(doc => doc.status === 'uploaded' || doc.status === 'not_available')
            .map(doc => doc.label) // Assuming label uniquely identifies a required slot
    );
    
    const requiredDocCount = requiredLabels.size; // Count unique required labels
    const completedDocCount = completedDocs.size; // Count unique completed labels

    // Combine field progress and document progress (example: 50/50 weight)
    const fieldProgress = (filledFields / totalFields) * 50;
    const docProgress = requiredDocCount > 0 ? (completedDocCount / requiredDocCount) * 50 : 50; // Avoid division by zero, give 50% if no docs required?

    return Math.round(fieldProgress + docProgress);
    // Old calculation based on uploadedDocuments.length:
    // const totalDocLabels = businessDocuments.reduce((sum, doc) => sum + doc.Labels.length, 0);
    // const uploadedLabels = uploadedDocuments.length; // This is no longer accurate for progress
    // return Math.round((filledFields / totalFields) * 100);
  };

  // Function to autosave the form data
  const autosaveFormData = async (field, value) => {
    if (!initialData || !initialData.id) {
      // If we don't have an ID yet, create the document first
      if (!prequalId) {
        await handleCreatePrequalification();
        return; // Return as handleCreatePrequalification will set prequalId, and we'll trigger field updates on subsequent changes
      }
    }
    
    const docId = initialData?.id || prequalId;
    if (!docId) return;
    
    setAutoSaving(true);
    try {
      const prequalRef = doc(db, 'prequalificationApplications', docId);
      const updateData = { [field]: value, lastUpdated: serverTimestamp() };
      
      // For currency fields, remove formatting before saving
      if (field === 'purchasePrice' || field === 'realEstateValue') {
        updateData[field] = value.replace(/[^0-9]/g, '');
      }
      
      // For boolean fields from radio buttons
      if (field === 'realEstateIncluded') updateData[field] = value === 'yes';
      if (field === 'hasOtherAddbacks') updateData[field] = value === 'yes';
      if (field === 'topCustomerConcentration') updateData[field] = value === 'yes';
      if (field === 'hasMultipleLLCs') updateData[field] = value === 'yes';
      
      await updateDoc(prequalRef, updateData);
    } catch (error) {
      console.error(`Error autosaving field ${field}:`, error);
      showNotification('error', `Failed to save ${field}. Please try manually saving.`);
    } finally {
      setAutoSaving(false);
    }
  };

  // Function to handle field changes with debounced autosave
  const handleFieldChange = (field, value) => {
    // Update local state immediately
    switch (field) {
      case 'businessName': setBusinessName(value); break;
      case 'businessState': setBusinessState(value); break;
      case 'industry': setIndustry(value); break;
      case 'purchasePrice': setPurchasePrice(formatCurrency(value)); break;
      case 'businessAge': setBusinessAge(value); break;
      case 'businessDescription': setBusinessDescription(value); break;
      case 'licenseNeeded': setLicenseNeeded(value); break;
      case 'realEstateIncluded': setRealEstateIncluded(value); break;
      case 'realEstateValue': setRealEstateValue(formatCurrency(value)); break;
      case 'hasOtherAddbacks': setHasOtherAddbacks(value); break;
      case 'topCustomerConcentration': setTopCustomerConcentration(value); break;
      case 'hasMultipleLLCs': setHasMultipleLLCs(value); break;
      default: break;
    }
    
    // Clear existing timer
    if (saveTimer) clearTimeout(saveTimer);
    
    // Set new timer for autosave
    const newTimer = setTimeout(() => {
      autosaveFormData(field, value);
    }, 1000); // 1 second debounce
    
    setSaveTimer(newTimer);
  };

  // Helper to get all currently required document labels
  const getRequiredDocumentLabels = () => {
    const requiredLabels = new Set();

    // Standard business documents
    businessDocuments.forEach(docDef => {
      // Handle tax returns (IDs 1, 2, 3) based on multiple LLCs
      if ([1, 2, 3].includes(docDef.DocumentID)) {
        if (docDef.Labels && docDef.Labels.length > 0) {
          requiredLabels.add(docDef.Labels[0]); // Always add the first label
        }
        if (hasMultipleLLCs === 'yes' && docDef.Labels && docDef.Labels.length > 1 && docDef.Labels[1].startsWith('Set 2')) {
          requiredLabels.add(docDef.Labels[1]); // Add the second "Set 2" label if multiple LLCs and label exists
        }
      } else {
        // For other standard documents, add all their labels
        docDef.Labels.forEach(label => requiredLabels.add(label));
      }
    });

    // Conditional documents
    if (licenseNeeded && conditionalDocuments.license) {
      conditionalDocuments.license.Labels.forEach(label => requiredLabels.add(label));
    }
    if (realEstateIncluded === 'yes' && conditionalDocuments.appraisal) {
      conditionalDocuments.appraisal.Labels.forEach(label => requiredLabels.add(label));
    }
    if (hasOtherAddbacks === 'yes' && conditionalDocuments.addbacks) {
      conditionalDocuments.addbacks.Labels.forEach(label => requiredLabels.add(label));
    }
    if (topCustomerConcentration === 'yes' && conditionalDocuments.customerConcentration) {
      conditionalDocuments.customerConcentration.Labels.forEach(label => requiredLabels.add(label));
    }

    // Note: 'otherDocuments.other' is typically not "required" for submission, so not included here.
    // If it were, you would add its labels:
    // otherDocuments.other.Labels.forEach(label => requiredLabels.add(label));

    return Array.from(requiredLabels);
  };

  // Helper to find document definition by DocumentID
  const findDocumentDefinitionById = (documentId) => {
    let docDef = businessDocuments.find(d => d.DocumentID === documentId);
    if (!docDef) {
      docDef = Object.values(conditionalDocuments).find(d => d.DocumentID === documentId);
    }
    if (!docDef && documentId === otherDocuments.other.DocumentID) {
      docDef = otherDocuments.other;
    }
    return docDef;
  };
  
  // Helper to find document definition by Label (more complex, as labels are not unique across DocumentIDs)
  // This simplified version assumes we can iterate and find the first match, suitable if labels are unique within their context of requirement.
  const findDocumentDefinitionByLabel = (labelToFind) => {
    const allDocDefinitions = [
      ...businessDocuments,
      ...(licenseNeeded ? [conditionalDocuments.license] : []),
      ...(realEstateIncluded === 'yes' ? [conditionalDocuments.appraisal] : []),
      ...(hasOtherAddbacks === 'yes' ? [conditionalDocuments.addbacks] : []),
      ...(topCustomerConcentration === 'yes' ? [conditionalDocuments.customerConcentration] : []),
      // Do not include otherDocuments.other unless it's made mandatory
    ];

    for (const docDef of allDocDefinitions) {
      if (docDef.Labels.includes(labelToFind)) {
        // Special handling for tax returns if not multiple LLCs
        if ([1,2,3].includes(docDef.DocumentID) && labelToFind.startsWith('Set 2') && hasMultipleLLCs !== 'yes') {
          continue; // Skip "Set 2" label if not multiple LLCs
        }
        return docDef;
      }
    }
    return null; // Should not happen if label comes from getRequiredDocumentLabels
  };


  // Function to check if all requirements for submission are met
  const checkAllRequirementsMet = () => {
    const missingFields = [];
    const missingDocuments = [];

    // 1. Check form fields
    if (!businessName.trim()) missingFields.push("Business Name");
    if (realEstateIncluded === null) missingFields.push("Real Estate Included selection");
    if (realEstateIncluded === 'yes' && !realEstateValue.replace(/\D/g, '')) missingFields.push("Value of Real Estate");
    if (hasOtherAddbacks === null) missingFields.push("Other Addbacks selection");
    if (hasMultipleLLCs === null) missingFields.push("Multiple LLCs selection");
    if (topCustomerConcentration === null) missingFields.push("Top Customer Concentration selection");

    // 2. Check documents
    const requiredLabels = getRequiredDocumentLabels();
    for (const label of requiredLabels) {
      const docDefinition = findDocumentDefinitionByLabel(label); // Find which DocumentID this label belongs to
      if (docDefinition) {
        const isUp = isDocumentUploaded(docDefinition.DocumentID, label);
        const isNA = isDocumentNotAvailable(docDefinition.DocumentID, label);
        if (!isUp && !isNA) {
          missingDocuments.push(label);
        }
      } else {
        // This case should ideally not be reached if getRequiredDocumentLabels is correct
        console.warn(`Could not find document definition for required label: ${label}`);
        missingDocuments.push(`${label} (definition not found)`);
      }
    }
    
    const allMet = missingFields.length === 0 && missingDocuments.length === 0;
    return { allMet, missingFields, missingDocuments };
  };

  // Effect to update isSubmittable state
  useEffect(() => {
    const { allMet } = checkAllRequirementsMet();
    // Application must also have an ID (been saved at least once) to be submittable
    const currentPrequalId = initialData?.id || prequalId;
    setIsSubmittable(allMet && !!currentPrequalId);

  }, [
    businessName, purchasePrice, businessAge, businessDescription, licenseNeeded, 
    realEstateIncluded, realEstateValue, hasOtherAddbacks, hasMultipleLLCs, 
    topCustomerConcentration, uploadedDocuments, initialData, prequalId // Listen to prequalId too
  ]);

  const handleSubmitApplication = async () => {
    setIsSubmitting(true);
    const currentPrequalId = initialData?.id || prequalId;

    if (!currentPrequalId) {
      showNotification('error', 'Please save the application before submitting.');
      setIsSubmitting(false);
      return;
    }

    const { allMet, missingFields, missingDocuments } = checkAllRequirementsMet();

    if (!allMet) {
      let message = "Please complete all required items before submitting: ";
      if (missingFields.length > 0) {
        message += `\nFields: ${missingFields.join(', ')}. `;
      }
      if (missingDocuments.length > 0) {
        message += `\nDocuments: ${missingDocuments.join(', ')}.`;
      }
      // Replace \n with <br /> for HTML display in notification, or use a multi-line supporting notification component
      showNotification('error', message.replace(/\n/g, '<br/>'));
      setIsSubmitting(false);
      return;
    }

    try {
      const prequalRef = doc(db, 'prequalificationApplications', currentPrequalId);
      await updateDoc(prequalRef, {
        businessName,
        businessState,
        industry,
        purchasePrice: purchasePrice.replace(/[^0-9]/g, ''),
        businessAge,
        businessDescription,
        licenseNeeded,
        realEstateIncluded: realEstateIncluded === 'yes',
        realEstateValue: realEstateIncluded === 'yes' ? realEstateValue.replace(/[^0-9]/g, '') : '',
        hasOtherAddbacks: hasOtherAddbacks === 'yes',
        topCustomerConcentration: topCustomerConcentration === 'yes',
        hasMultipleLLCs: hasMultipleLLCs === 'yes',
        status: 'underReview', // Update status
        lastUpdated: serverTimestamp(),
        // documents array is no longer directly managed here
      });
      
      showNotification('success', 'Application submitted successfully for review!');
      onCancel(); // Return to list view
    } catch (error) {
      console.error('Error submitting prequalification:', error);
      showNotification('error', 'Error submitting prequalification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-gradient-to-br from-white to-indigo-50 rounded-xl shadow-xl overflow-hidden">
        {/* Header with progress bar */}
        <div className="relative bg-indigo-700 px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-white">
                Business Prequalification
              </h2>
              <p className="text-indigo-200 mt-1">
                Complete this form to prequalify a business for an SBA loan
              </p>
            </div>
            <button
              onClick={onCancel}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700"
            >
              Cancel
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-indigo-100">Completion Progress</span>
              <span className="text-sm font-medium text-indigo-100">{calculateProgress()}%</span>
            </div>
            <div className="w-full bg-indigo-900 rounded-full h-2.5">
              <div 
                className="bg-indigo-300 h-2.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* Steps navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-8 py-4">
            <nav className="flex">
              <button 
                onClick={() => setActiveStep(1)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg mr-2 ${
                  activeStep === 1 
                    ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-700' 
                    : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                1. Business Information
              </button>
              <button 
                onClick={() => {
                  // Basic validation before proceeding to documents
                  if (!businessName || realEstateIncluded === null || hasOtherAddbacks === null) {
                    alert("Please complete all required fields in Business Information first (Business Name, Real Estate Inclusion, Other Addbacks).");
                    return;
                  }
                  // Additional validation for real estate value if included
                  if (realEstateIncluded === 'yes' && !realEstateValue) {
                     alert("Please enter the Value of Real Estate.");
                     return;
                  }
                  setActiveStep(2);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeStep === 2 
                    ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-700' 
                    : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                2. Required Documents
              </button>
            </nav>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8">
          {/* Step 1: Business Information */}
          {activeStep === 1 && (
            <div className="space-y-8 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full mr-3 text-sm font-bold">1</span>
                  Business Details
                  {autoSaving && (
                    <span className="ml-3 text-xs font-normal text-indigo-600 flex items-center">
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Autosaving...
                    </span>
                  )}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="business-name"
                      value={businessName}
                      onChange={(e) => handleFieldChange('businessName', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter business name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="business-state" className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      id="business-state"
                      value={businessState}
                      onChange={(e) => handleFieldChange('businessState', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="">Select State</option>
                      {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <input
                      type="text"
                      id="industry"
                      value={industry}
                      onChange={(e) => handleFieldChange('industry', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="e.g., Restaurant, Manufacturing, Retail"
                    />
                  </div>

                  <div>
                    <label htmlFor="purchase-price" className="block text-sm font-medium text-gray-700 mb-1">
                      Total Purchase price (Including real estate)
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <input
                        type="text"
                        id="purchase-price"
                        value={purchasePrice}
                        onChange={(e) => handleFieldChange('purchasePrice', e.target.value)}
                        className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="$0"
                      />
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="business-age" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Age
                    </label>
                    <input
                      type="text"
                      id="business-age"
                      value={businessAge}
                      onChange={(e) => handleFieldChange('businessAge', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter business age (e.g., 5 years)"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label htmlFor="business-description" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Description (May Skip If covered in CIM) 
                    </label>
                    <textarea
                      id="business-description"
                      value={businessDescription}
                      onChange={(e) => handleFieldChange('businessDescription', e.target.value)}
                      rows={8}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Provide a concise overview of the business, including its core operations, industry sector, and key differentiators. If a Confidential Information Memorandum (CIM) is being uploaded, this section may be used to summarize crucial points or add supplementary details not included in the CIM."
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <div className="flex items-center">
                      <input
                        id="license-needed"
                        type="checkbox"
                        checked={licenseNeeded}
                        onChange={(e) => handleFieldChange('licenseNeeded', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="license-needed" className="ml-2 block text-sm font-medium text-gray-700">
                        License needed for this business?
                      </label>
                    </div>
                  </div>

                  {/* Real Estate Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Is there real estate included in the deal? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Real estate included</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="real-estate-yes"
                            name="real-estate-included"
                            type="radio"
                            value="yes"
                            checked={realEstateIncluded === 'yes'}
                            onChange={(e) => handleFieldChange('realEstateIncluded', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="real-estate-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="real-estate-no"
                            name="real-estate-included"
                            type="radio"
                            value="no"
                            checked={realEstateIncluded === 'no'}
                            onChange={(e) => handleFieldChange('realEstateIncluded', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="real-estate-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  {/* Conditional Real Estate Value Input */}
                  {realEstateIncluded === 'yes' && (
                    <div>
                      <label htmlFor="real-estate-value" className="block text-sm font-medium text-gray-700 mb-1">
                        Value of Real Estate <span className="text-red-500">*</span>
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <input
                          type="text"
                          id="real-estate-value"
                          value={realEstateValue}
                          onChange={(e) => handleFieldChange('realEstateValue', e.target.value)}
                          className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="$0"
                          required
                        />
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Multiple LLCs Question - Added */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Does the business have more than one LLC / set of tax returns?
                      <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Multiple LLCs</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="multiple-llc-yes"
                            name="multiple-llc-included"
                            type="radio"
                            value="yes"
                            checked={hasMultipleLLCs === 'yes'}
                            onChange={(e) => handleFieldChange('hasMultipleLLCs', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="multiple-llc-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="multiple-llc-no"
                            name="multiple-llc-included"
                            type="radio"
                            value="no"
                            checked={hasMultipleLLCs === 'no'}
                            onChange={(e) => handleFieldChange('hasMultipleLLCs', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="multiple-llc-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                     {hasMultipleLLCs === 'yes' && (
                       <p className="mt-2 text-sm text-indigo-700">
                         Please provide documents for both sets in Step 2.
                       </p>
                     )}
                  </div>

                  {/* Addbacks Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Are there any addbacks besides Interest, Depreciation, Amortization and Officer's Compensation? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Other Addbacks Included</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="addbacks-yes"
                            name="other-addbacks-included"
                            type="radio"
                            value="yes"
                            checked={hasOtherAddbacks === 'yes'}
                            onChange={(e) => handleFieldChange('hasOtherAddbacks', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="addbacks-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="addbacks-no"
                            name="other-addbacks-included"
                            type="radio"
                            value="no"
                            checked={hasOtherAddbacks === 'no'}
                            onChange={(e) => handleFieldChange('hasOtherAddbacks', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="addbacks-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                     {hasOtherAddbacks === 'yes' && (
                       <p className="mt-2 text-sm text-indigo-700">
                         Please upload a document outlining these addbacks in Step 2. Addbacks must be verifiable.
                       </p>
                     )}
                  </div>

                  {/* Top 3 Customers Question */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Do the top 3 customers make up more than 15% of revenue? <span className="text-red-500">*</span>
                    </label>
                    <fieldset className="mt-2">
                      <legend className="sr-only">Customer Concentration</legend>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <input
                            id="customer-concentration-yes"
                            name="customer-concentration"
                            type="radio"
                            value="yes"
                            checked={topCustomerConcentration === 'yes'}
                            onChange={(e) => handleFieldChange('topCustomerConcentration', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="customer-concentration-yes" className="ml-3 block text-sm font-medium text-gray-700">
                            Yes
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="customer-concentration-no"
                            name="customer-concentration"
                            type="radio"
                            value="no"
                            checked={topCustomerConcentration === 'no'}
                            onChange={(e) => handleFieldChange('topCustomerConcentration', e.target.value)}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="customer-concentration-no" className="ml-3 block text-sm font-medium text-gray-700">
                            No
                          </label>
                        </div>
                      </div>
                    </fieldset>
                    {topCustomerConcentration === 'yes' && (
                      <p className="mt-2 text-sm text-indigo-700">
                        Please upload a document in Step 2 listing your top 10 customers by percentage of revenue.
                      </p>
                    )}
                  </div>

                </div>
              </div>
              
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onCancel}
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                     // Basic validation before proceeding
                     if (!businessName || realEstateIncluded === null || hasOtherAddbacks === null) {
                       alert("Please complete all required fields in Business Information first (Business Name, Real Estate Inclusion, Other Addbacks).");
                       return;
                     }
                     // Additional validation for real estate value if included
                     if (realEstateIncluded === 'yes' && !realEstateValue) {
                        alert("Please enter the Value of Real Estate.");
                        return;
                     }
                     setActiveStep(2);
                  }}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Continue to Documents
                </button>
              </div>
            </div>
          )}
          
          {/* Step 2: Required Documents */}
          {activeStep === 2 && (
            <div className="space-y-8 max-w-5xl mx-auto">
              {/* Bulk upload CTA */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900">Have a bunch of files?</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Upload multiple files or a whole folder — we'll tag each one for you.</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                    Upload files
                    <input type="file" multiple className="hidden"
                      onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
                  </label>
                  <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                    Upload folder
                    <input type="file" multiple webkitdirectory="" directory="" className="hidden"
                      onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
                  </label>
                </div>
              </div>

              {(() => {
                const docList = [
                  ...businessDocuments,
                  ...(licenseNeeded ? [conditionalDocuments.license] : []),
                  ...(realEstateIncluded === 'yes' ? [conditionalDocuments.appraisal] : []),
                  ...(hasOtherAddbacks === 'yes' ? [conditionalDocuments.addbacks] : []),
                  ...(topCustomerConcentration === 'yes' ? [conditionalDocuments.customerConcentration] : []),
                ];
                const checklistItems = docList.flatMap(doc =>
                  doc.Labels
                    .filter(l => !(l.startsWith('Set 2') && hasMultipleLLCs !== 'yes'))
                    .map(label => ({ docId: doc.DocumentID, displayName: doc.displayName, label }))
                );
                const uploadedCount = checklistItems.filter(item => isDocumentUploaded(item.docId, item.label) || isDocumentNotAvailable(item.docId, item.label)).length;
                const requiredComplete = uploadedCount === checklistItems.length;
                const docOptionsForDropdown = buildPrequalDocOptions();
                return (
                  <>
                    {/* Progress checklist */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Required Business Documents</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${requiredComplete ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {uploadedCount} / {checklistItems.length}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {checklistItems.map(item => {
                          const isUp = isDocumentUploaded(item.docId, item.label);
                          const isNA = isDocumentNotAvailable(item.docId, item.label);
                          return (
                            <div key={`${item.docId}-${item.label}`} className="flex items-center gap-2 text-xs">
                              {isUp ? <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" /> :
                                isNA ? <div className="h-4 w-4 rounded-full bg-gray-200 flex-shrink-0" /> :
                                <div className="h-4 w-4 rounded-full border-2 border-orange-300 flex-shrink-0" />}
                              <span className={isUp ? 'text-green-700' : isNA ? 'text-gray-400 line-through' : 'text-gray-600'}>{item.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Single upload form */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload a Document</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                          <select
                            value={simpleSelectedLabel}
                            onChange={(e) => setSimpleSelectedLabel(e.target.value)}
                            disabled={!!simpleCustomLabel}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                          >
                            <option value="">Select document type...</option>
                            {docOptionsForDropdown.map(opt => (
                              <option key={`${opt.displayName}-${opt.label}`} value={opt.label}>{opt.displayName} — {opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Or use a custom name (overrides selection)</label>
                          <input
                            type="text"
                            value={simpleCustomLabel}
                            onChange={(e) => setSimpleCustomLabel(e.target.value)}
                            placeholder="e.g. Franchise Disclosure Document"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                          <input
                            id="prequal-file-upload"
                            type="file"
                            onChange={(e) => setSimpleSelectedFile(e.target.files[0])}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSimpleUpload}
                          disabled={simpleUploading || !simpleSelectedFile || (!simpleSelectedLabel && !simpleCustomLabel.trim())}
                          className="w-full py-2.5 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <CloudArrowUpIcon className="h-5 w-5" />
                          {simpleUploading ? 'Uploading...' : 'Upload Document'}
                        </button>
                      </div>

                      {/* Uploaded files list */}
                      {uploadedDocuments.filter(d => d.status === 'uploaded').length > 0 && (
                        <div className="mt-6 border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Uploaded files ({uploadedDocuments.filter(d => d.status === 'uploaded').length})</h4>
                          <div className="space-y-1">
                            {uploadedDocuments.filter(d => d.status === 'uploaded').map((f, i) => (
                              <div key={f.id || i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-2 border border-gray-100">
                                <div className="flex items-center gap-2 min-w-0">
                                  <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  <span className="font-medium text-gray-700 truncate">{f.label}</span>
                                  <span className="text-gray-400 truncate">— {f.fileName}</span>
                                </div>
                                {f.fileUrl && (
                                  <a href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 flex-shrink-0">View</a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
              {false && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hidden">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full mr-3 text-sm font-bold">2</span>
                  Required Business Documents
                </h3>

                {/* Document sections without accordion */}
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg shadow-sm bg-white">
                  {[
                    ...businessDocuments,
                    ...(licenseNeeded ? [conditionalDocuments.license] : []),
                    ...(realEstateIncluded === 'yes' ? [conditionalDocuments.appraisal] : []),
                    ...(hasOtherAddbacks === 'yes' ? [conditionalDocuments.addbacks] : []),
                    ...(topCustomerConcentration === 'yes' ? [conditionalDocuments.customerConcentration] : []),
                    otherDocuments.other // Include the new document type here
                  ].map((document, index) => {
                    // Calculate uploaded count for documents with multiple labels
                    // For single-label docs (like the updated 2024 Financials), this logic still works.
                    const completedCount = document.Labels.reduce((count, label) => {
                      const isUp = isDocumentUploaded(document.DocumentID, label);
                      const isNA = isDocumentNotAvailable(document.DocumentID, label);
                      return count + ((isUp || isNA) ? 1 : 0);
                    }, 0);
                    const totalLabels = document.Labels.length;
                    const isComplete = completedCount === totalLabels;

                    return (
                      <div key={document.DocumentID} className="bg-white first:rounded-t-lg last:rounded-b-lg">
                        {/* Document Section */}
                        <div className="px-6 py-4">
                          {/* --- Modification Start: Conditional Label Rendering --- */}
                          {document.Labels
                            .filter((label, index) => {
                              // For docs 1, 2, 3:
                              if ([1, 2, 3].includes(document.DocumentID)) {
                                // Always show the first label (index 0)
                                if (index === 0) {
                                  return true;
                                }
                                // Show the second label (index 1, starts with "Set 2") only if multiple LLCs is 'yes'
                                if (index === 1 && label.startsWith('Set 2') && hasMultipleLLCs === 'yes') {
                                  return true;
                                }
                                // Otherwise, hide the second label for docs 1, 2, 3
                                return false;
                              }
                              // For all other documents, show all their labels
                              return true;
                            })
                            .map((label) => {
                          {/* --- Modification End --- */}

                            const uploadKey = `${document.DocumentID}-${label}`;
                            const currentStatus = uploadStatuses[uploadKey] || 'idle';
                            const isUploaded = isDocumentUploaded(document.DocumentID, label);
                            const fileUrl = getDocumentFileUrl(document.DocumentID, label);
                            const isNA = isDocumentNotAvailable(document.DocumentID, label);

                            // --- Modification Start: Handle multiple files display for 'Other' ---
                            const isOtherDocType = document.DocumentID === otherDocuments.other.DocumentID;
                            const otherUploadedFiles = isOtherDocType
                              ? uploadedDocuments.filter(
                                  doc =>
                                    doc.documentName === document.documentName &&
                                    doc.label === label &&
                                    doc.status === 'uploaded'
                                )
                              : [];
                            // --- Modification End ---

                            return (
                              <div key={label} className="py-4">
                                {/* Wrapper div for label and action buttons */} 
                                <div 
                                  className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${ 
                                    isNA ? 'bg-gray-50 border-gray-200' : 
                                    currentStatus === 'uploading' ? 'bg-blue-50 border-blue-200 animate-pulse' : 
                                    isUploaded ? 'bg-emerald-50 border-emerald-200' : // Keep green background if *any* file is uploaded for 'Other'
                                    'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30' // Subtle indigo hover
                                  } ${ 
                                    !isNA && currentStatus !== 'uploading' ? 'hover:shadow-sm' : '' 
                                  }`}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.add('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm'); // Indigo highlight on drag
                                    }
                                  }}
                                  onDragLeave={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.remove('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm');
                                    }
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    if (!isNA && currentStatus !== 'uploading') {
                                      e.currentTarget.classList.remove('border-indigo-300', 'bg-indigo-50/30', 'shadow-sm');
                                      const file = e.dataTransfer.files[0];
                                      if (file) {
                                        const fakeEvent = { target: { files: [file] } };
                                        handleFileUpload(fakeEvent, document.DocumentID, label);
                                      }
                                    }
                                  }}
                                >
                                  <label
                                    htmlFor={`file-${document.DocumentID}-${label}`}
                                    className={`flex-1 flex items-center ${isNA || currentStatus === 'uploading' ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                                  >
                                    <div className="flex items-center">
                                      {/* Icon changes based on state */}
                                      {isUploaded ? (
                                        <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 text-emerald-500" />
                                      ) : currentStatus === 'uploading' ? (
                                        <CloudArrowUpIcon className="h-6 w-6 mr-3 flex-shrink-0 text-blue-500 animate-bounce" />
                                      ) : (
                                        <DocumentTextIcon className={`h-6 w-6 mr-3 flex-shrink-0 ${ 
                                          isNA ? 'text-gray-400' : 'text-indigo-500' // Subtle indigo icon default
                                        }`} />
                                      )}
                                      <div>
                                        <span className={`text-sm font-medium ${ 
                                          isNA ? 'text-gray-500' : 
                                          currentStatus === 'uploading' ? 'text-blue-700' : 
                                          isUploaded ? 'text-emerald-700' : 
                                          'text-gray-800' // Slightly darker default text
                                        }`}>
                                          {label}
                                        </span>
                                        {/* Status text indicators */}
                                        {currentStatus === 'uploading' && <span className="text-xs ml-2 text-blue-600">(Uploading...)</span>}
                                        {/* Don't show filename here for 'Other' docs; it's listed below */}
                                        {!isOtherDocType && isUploaded && (
                                          <span className="text-xs ml-2 text-emerald-600">
                                            ({uploadedDocuments.find(doc => 
                                              doc.documentName === getGeneralDocumentName(document) && 
                                              doc.label === label && 
                                              doc.status === 'uploaded'
                                            )?.fileName || 'Uploaded'})
                                          </span>
                                        )}
                                        {isNA && <span className="text-xs ml-2 text-gray-500">(Not Available)</span>}
                                      </div>
                                    </div>
                                  </label>

                                  {/* Action Buttons */}
                                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                    {/* View Button (Only for single-file types) */}
                                    {!isOtherDocType && fileUrl && isUploaded && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); window.open(fileUrl, '_blank'); }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        View
                                      </button>
                                    )}
                                    {/* Upload Button (Always show for 'Other' if not N/A, conditionally for others) */}
                                    {(!isNA && !isUploaded || isOtherDocType) && currentStatus !== 'uploading' && (
                                      <label
                                        htmlFor={`file-${document.DocumentID}-${label}`}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors cursor-pointer"
                                      >
                                        {/* Change button text for 'Other' docs if files already exist */}
                                        {isOtherDocType && isUploaded ? 'Upload More' : 'Upload'}
                                      </label>
                                    )}
                                    {/* N/A Button */}
                                    {!isNA && currentStatus !== 'uploading' && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); handleDoNotHave(document.DocumentID, label); }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        N/A
                                      </button>
                                    )}
                                    {/* Enable Button */}
                                    {isNA && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setUploadStatuses(prev => { const newState = { ...prev }; delete newState[uploadKey]; return newState; });
                                          // --- Modification Start: Clear N/A marker for 'Other' ---
                                          // We need to remove the N/A document from the Firestore collection and refetch
                                          const docToRemove = uploadedDocuments.find(doc => doc.documentName === document.documentName && doc.label === label && doc.status === 'not_available'); // Match using document.documentName
                                          if (docToRemove && docToRemove.id) {
                                            // --- Finalized: Uncomment and implement delete logic --- 
                                            deleteDoc(doc(db, 'prequalificationMetadata', docToRemove.id))
                                              .then(() => {
                                                showNotification('info', `'${label}' marked as available again.`);
                                                fetchPrequalificationMetadata(prequalId); // Refresh state after delete
                                              })
                                              .catch(err => {
                                                console.error("Error removing N/A marker:", err);
                                                showNotification('error', 'Failed to re-enable document.');
                                                // Optional: Revert local status if delete failed
                                                setUploadStatuses(prev => ({ ...prev, [uploadKey]: 'not_available' })); 
                                              });
                                            // --- End Finalized ---
                                            // Removed previous console.warn and local optimistic update
                                          } else {
                                               showNotification('info', `'${label}' marked as available again (local state).`); // This case might indicate a state sync issue
                                          }
                                          // --- Modification End ---
                                        }}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                      >
                                        Re-enable
                                      </button>
                                    )}
                                  </div>

                                  <input
                                    id={`file-${document.DocumentID}-${label}`}
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(e, document.DocumentID, label)}
                                    accept={document.AcceptedFileTypes || "*/*"}
                                    disabled={currentStatus === 'uploading' || isNA}
                                  />
                                </div>
                                  
                                {/* --- Modification Start: List uploaded files for 'Other' --- */}
                                {isOtherDocType && otherUploadedFiles.length > 0 && (
                                  <div className="mt-3 pl-10 pr-4 space-y-2">
                                    <p className="text-xs font-medium text-gray-600">Uploaded files:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                      {otherUploadedFiles.map((file, idx) => (
                                        <li key={idx} className="text-sm text-gray-800 flex justify-between items-center">
                                          <span>{file.fileName}</span>
                                          <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); window.open(file.fileUrl, '_blank'); }}
                                            className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                                          >
                                            View
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setActiveStep(1)}
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Back to Business Info
                </button>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting} // Disable if submitting too
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save Prequalification'
                    )}
                  </button>
                  {/* {{ edit_4 }} */}
                  <button
                    type="button"
                    onClick={handleSubmitApplication}
                    disabled={!isSubmittable || isLoading || isSubmitting}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Bulk upload modal */}
      {bulkQueue.length > 0 && (() => {
        const docOptions = buildPrequalDocOptions();
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Tag {bulkQueue.length} {bulkQueue.length === 1 ? 'file' : 'files'}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">We tried to guess each doc type from the filename — fix any that look wrong.</p>
                </div>
                <button onClick={closeBulkModal} disabled={bulkUploading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <span className="text-xl leading-none">×</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {bulkQueue.map((row, idx) => (
                  <div key={idx} className={`border rounded-lg p-3 ${
                    row.status === 'done' ? 'border-green-200 bg-green-50' :
                    row.status === 'error' ? 'border-red-200 bg-red-50' :
                    row.status === 'uploading' ? 'border-indigo-200 bg-indigo-50' :
                    'border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <DocumentTextIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-800 truncate">{row.file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{(row.file.size / 1024).toFixed(0)} KB</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {row.status === 'done' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                        {row.status === 'uploading' && <span className="text-xs text-indigo-600 animate-pulse">Uploading…</span>}
                        {row.status !== 'done' && row.status !== 'uploading' && (
                          <button onClick={() => removeBulkRow(idx)} disabled={bulkUploading} className="text-gray-400 hover:text-red-500 disabled:opacity-30">
                            <span className="text-base leading-none">×</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <select
                      value={row.label}
                      onChange={(e) => updateBulkRow(idx, { label: e.target.value })}
                      disabled={bulkUploading || row.status === 'done' || !!row.customLabel}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                    >
                      <option value="">Select document type…</option>
                      {docOptions.map(opt => (
                        <option key={`${opt.displayName}-${opt.label}`} value={opt.label}>{opt.displayName} — {opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={row.customLabel}
                      onChange={(e) => updateBulkRow(idx, { customLabel: e.target.value })}
                      placeholder="…or type a custom name (overrides above)"
                      disabled={bulkUploading || row.status === 'done'}
                      className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                    />
                    {row.error && <p className="mt-2 text-xs text-red-600">{row.error}</p>}
                  </div>
                ))}
              </div>
              <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {bulkQueue.filter(r => r.status === 'done').length} of {bulkQueue.length} uploaded
                </p>
                <div className="flex gap-2">
                  <button onClick={closeBulkModal} disabled={bulkUploading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    {bulkQueue.every(r => r.status === 'done') ? 'Done' : 'Cancel'}
                  </button>
                  <button onClick={startBulkUpload}
                    disabled={bulkUploading || bulkQueue.every(r => r.status === 'done')}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    {bulkUploading ? 'Uploading…' : 'Upload all'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PrequalificationsTab;
